/**
 * The expense (reimbursement) contract — what an employee claims back, and the approval it starts.
 *
 * Copies leave.contract.ts's conventions exactly (see employee.contract.ts's header for the full
 * rationale): DTOs distinct from the Prisma models, every DTO carrying what `ResourceTarget` needs
 * to authorise it, and `paginatedSchema()` for every list.
 *
 * FIRST SLICE, DELIBERATELY NARROW. Read, create a draft, and submit it. Advances and net-off,
 * trips, clients, colleague tagging, settlement, approve/reject, and receipt presigning are later
 * task groups in REIMBURSEMENT_PLAN.md — the DTO is shaped so each of those ADDS fields rather than
 * reshaping the ones here. Nothing in this file is implemented yet; per CLAUDE.md invariant 8 the
 * contract lands on its own before any route, schema, or screen consumes it.
 *
 * MONEY IS TWO FLAT FIELDS, NOT A NESTED OBJECT. `amount` + `currency` mirror the two Postgres
 * columns (`numeric(18,4)` and `varchar(3)`) and read as `claim.amount`, not `claim.amount.amount`.
 * `decimalStringSchema` is reused verbatim — a decimal string on the wire, parsed with decimal.js,
 * never a float (invariant 2).
 *
 * APPROVAL GOES THROUGH `@hrms/workflow`, NEVER A HAND-ROLLED STATUS FLIP (invariant 6).
 * `expenseClaimSchema.status` mirrors whatever the underlying `WorkflowInstance` says, exactly as
 * leave's does; `submitClaim` is the route that starts that instance.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { EXPENSE_CATEGORY_KINDS } from '../enums.js';
import { isValidGstin } from '../gst.js';
import {
  bankFileFormatSchema,
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  positiveDecimalStringSchema,
  errorBodySchema,
  expenseAdvanceStatusSchema,
  expenseCategoryUsageTypeSchema,
  expenseClaimStatusSchema,
  expenseMileageUnitSchema,
  expensePaymentBatchStatusSchema,
  expensePolicyRulePeriodSchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  paidModeSchema,
  payoutModeSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * Every financial mutation requires this (invariant 10) — a retried request must return the
 * original result, never execute twice. Declared here, on the contract, rather than left as an
 * Express-only convention `apps/api/src/middleware/idempotency.ts` enforces silently: ts-rest only
 * types a `headers` field on a client call when the ROUTE ITSELF declares one (see
 * `@ts-rest/core`'s `ClientInferRequestBase` — `THeaders` resolves to `never` otherwise), so
 * without this, `apps/web`/`apps/mobile` have no typed way to SEND the header at all, and every
 * real call from a UI 400s with "requires an Idempotency-Key header" no matter how correct the
 * body is. `leave.contract.ts` has the same gap — REIMBURSEMENT_PLAN.md's task T8.10 anticipated
 * needing exactly this check; fixing it here rather than there keeps this PR to this module.
 */
const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

// ---------------------------------------------------------------------------
// Module primitives
// ---------------------------------------------------------------------------

/**
 * An Indian GSTIN: 2-digit state code, the holder's 10-character PAN, an entity number, a fixed 'Z',
 * and a checksum character. Validated here because a malformed GSTIN on a claim is what makes the
 * input-tax credit unclaimable later — a failure that surfaces at filing time, months after the
 * claim was approved, when it is far more expensive to fix.
 *
 * Not PII under invariant 7: a GSTIN identifies the *merchant*, is printed on every invoice they
 * issue, and is publicly searchable on the GST portal. The employee's own PAN is a different field
 * on a different model, and that one is encrypted.
 */
export const gstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/,
    'Must be a valid 15-character GSTIN, e.g. 27AABCU9603R1ZM',
  )
  // R-P4 (Keka gap row 28, 2026-09-26) — was pattern-only ("looks the right shape"); now also
  // verifies the check character with the same mod-36 algorithm the GSTN portal uses, so a single
  // mistyped or transposed character is refused instead of silently accepted.
  .refine((value) => isValidGstin(value), 'That GSTIN\'s check character does not match — check for a typo');

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * A spend category, with the policy the designs surface while the employee is still typing
 * (Policy Cap per head, receipt required above a threshold).
 *
 * `maxAmount` and `requiresReceipt` are carried on the DTO so the client can warn BEFORE
 * submitting, but the client is not the enforcer: both are re-checked server-side at submit, which
 * is the only check that counts (FR-8 / F4). A null `maxAmount` means the category is uncapped.
 */
/** One field the admin has attached to a category (R-P2, Keka gap row 12). Rendered by the claim
 * form, validated server-side against this same definition; values live on the claim as a
 * `key -> value` record shaped by these definitions. */
export const expenseCustomAttributeDefSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'select']),
  required: z.boolean(),
  options: z.array(z.string()).optional(),
});
export type ExpenseCustomAttributeDef = z.infer<typeof expenseCustomAttributeDefSchema>;

/** R-P6 — one claim's answers to its category's `customAttributeDefs`, keyed by `key`. Every value
 * is a plain string regardless of the def's `type` ('number' and 'select' are still typed and
 * validated as strings the same way `amount`/`expenseDate` are — a decimal or a date is also just a
 * string on the wire); the route validates against the definitions, a Zod schema alone cannot. */
export const expenseCustomAttributeValuesSchema = z.record(z.string(), z.string());

/** `EXPENSE_CATEGORY_KINDS` (Travel Desk, TD-4.1), declared from the enum rather than imported from
 * `travel/money-schemas.ts` so the expense contract does not depend on the travel module. */
const expenseCategoryKindSchema = z.enum(EXPENSE_CATEGORY_KINDS);

/** EXP-P2-11 (Keka parity) — see `expenseCategorySchema.taxTreatment`'s own comment. */
const expenseCategoryTaxTreatmentSchema = z.enum(['exempt', 'exempt_with_limit', 'taxable']);
const expenseExemptionPeriodSchema = z.enum(['monthly', 'annual']);

export const expenseCategorySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  maxAmount: decimalStringSchema.nullable(),
  /** A monthly ceiling on the SUM of every claim in this category, org-wide, in the current UTC
   * calendar month — distinct from `maxAmount`, which caps one claim. Null means uncapped. */
  monthlyBudget: decimalStringSchema.nullable(),
  requiresReceipt: z.boolean(),
  /** Free-text GL/ledger account code, e.g. "6210-Travel" — Finance's own accounting system's
   * vocabulary, not a foreign key into one this module owns. Null means unmapped. */
  glAccountCode: z.string().nullable(),
  isActive: z.boolean(),
  /** R-P2 — actual (a real bill), mileage, or per_diem. Governs whether the claim form asks for an
   * amount directly or computes one from a rate (R-P6). */
  usageType: expenseCategoryUsageTypeSchema,
  customAttributeDefs: z.array(expenseCustomAttributeDefSchema),
  itcEligible: z.boolean(),
  defaultGstRate: decimalStringSchema.nullable(),
  sacHsnCode: z.string().nullable(),
  /** Travel Desk (TD-4.1) — what the category is for: `other` for every ordinary category; travel
   * files its policy-priced allowance drafts under the category of the matching kind. Distinct from
   * `usageType` (how the claim form computes the amount). Optional so the DTO stays additive; a
   * missing value reads as `other`. */
  kind: expenseCategoryKindSchema.optional(),
  /** Travel Desk (TD-4.1) — the currency `maxAmount` / `monthlyBudget` are expressed in. Null is
   * the organisation's base currency. Optional so the DTO stays additive. */
  currency: currencySchema.nullable().optional(),
  /** FBP F3 — the `SalaryComponent` (a flexi component, e.g. `FBP_LTA`) this category's claims
   * reconcile against in the employee's Flexible Benefits Plan. Null/omitted means the category is
   * not linked to any flexi component — today's behaviour for every category. Catalogue link only:
   * this field does not itself change claim submission, settlement, or tax treatment (that
   * reconciliation is FBP F4). Optional so the DTO stays additive. */
  flexiComponentId: uuidSchema.nullable().optional(),
  /** EXP-P2-11 (Keka parity) — whether a settled claim in this category is taxable income for the
   * employee. `exempt` is this module's own behaviour before this field existed, and the default
   * for every category that has not opted in. */
  taxTreatment: expenseCategoryTaxTreatmentSchema,
  /** Only meaningful when `taxTreatment` is `exempt_with_limit`; null otherwise. */
  exemptionLimit: decimalStringSchema.nullable(),
  /** `monthly` | `annual` — the window `exemptionLimit` resets over. Null unless `taxTreatment` is
   * `exempt_with_limit`. */
  exemptionPeriod: expenseExemptionPeriodSchema.nullable(),
});
export type ExpenseCategory = z.infer<typeof expenseCategorySchema>;

/**
 * One claim.
 *
 * Carries what `ResourceTarget` needs via `personId` — see convention 2 in employee.contract.ts.
 * `personId` is ALWAYS a Person id; the route must still resolve the manager chain from it the way
 * leave's `resolveTarget` does, because the row alone does not carry the claimant's current
 * manager.
 *
 * `reference` is the human-readable id every design shows on the card and the detail header — now
 * settled as `CLM-2026-00042`: the plan's `RMB-` prefix never appeared in a design, a mockup, or
 * any UI built against this contract, so the format every screen and the seed data already commit
 * to is the real one (docs/reimbursement/README.md records the decision). It is NULLABLE because it
 * is allocated from a per-org counter at submit, not at draft creation — burning a sequence number
 * on a draft that gets deleted before ever being sent for approval would leave a visible gap in
 * what is supposed to read as a continuous ledger.
 */

/**
 * Split-bill (2026-09-28, Keka parity) — one portion of a claim whose amount is divided across
 * several categories (e.g. a hotel folio: room charge under Lodging + food under Meals). Each
 * split is checked against ITS OWN category's cap/monthlyBudget/policy rule independently — the
 * entire point of the feature. `merchantGstin`/`billedToGstin`/`placeOfSupply` are NOT repeated
 * here — they live on the parent claim (one bill, one merchant), only the taxable-amount/rate/
 * eligibility inputs are per-split, since ITC eligibility and the GST rate are category-level
 * facts. `mileage`/`per_diem` categories can never appear in a split set — there is no free "bill
 * total" to divide when the amount is server-computed from distance/dates.
 */
export const expenseClaimSplitSchema = z.object({
  categoryId: uuidSchema,
  categoryName: z.string(),
  amount: decimalStringSchema,
  gstAmount: decimalStringSchema.nullable(),
  taxableAmount: decimalStringSchema.nullable(),
  gstRate: decimalStringSchema.nullable(),
  cgstAmount: decimalStringSchema.nullable(),
  sgstAmount: decimalStringSchema.nullable(),
  igstAmount: decimalStringSchema.nullable(),
  cessAmount: decimalStringSchema.nullable(),
  itcEligible: z.boolean().nullable(),
  /** v1: always null — see the schema's own comment on why per-split "approve less" is deferred. */
  approvedAmount: decimalStringSchema.nullable(),
  note: z.string().nullable(),
  customAttributeValues: expenseCustomAttributeValuesSchema,
});
export type ExpenseClaimSplit = z.infer<typeof expenseClaimSplitSchema>;

const expenseClaimSplitInputSchema = z.object({
  categoryId: uuidSchema,
  amount: positiveDecimalStringSchema,
  gstAmount: positiveDecimalStringSchema.optional(),
  note: z.string().trim().max(500).optional(),
  customAttributeValues: expenseCustomAttributeValuesSchema.optional(),
});

export const expenseClaimSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  reference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  /** ADDED 2026-09-28 — who actually filed this claim, which may differ from `personId` (Keka
   * parity: a manager/HR admin filing on someone else's behalf). Always set, even for a self-filed
   * claim — same "always set, even for self" convention as `LeaveRequest.filedByPersonId`, which
   * also exposes only the id, not a display name (the caller already has the org's people list). */
  filedByPersonId: uuidSchema,
  categoryId: uuidSchema,
  categoryName: z.string(),
  /** The date the money was actually spent — a calendar date, never an instant (invariant 3). */
  expenseDate: dateOnlySchema,
  amount: decimalStringSchema,
  currency: currencySchema,
  /** The GST already included in `amount`, in the same currency. Null when the bill carries none. */
  gstAmount: decimalStringSchema.nullable(),
  /** The merchant's GSTIN, not the employee's. */
  merchantGstin: gstinSchema.nullable(),
  merchantName: z.string(),
  billNumber: z.string().nullable(),
  /** How the employee paid. How the company pays them back is `payoutMode`, set at settlement. */
  paidMode: paidModeSchema.nullable(),
  businessPurpose: z.string(),
  status: expenseClaimStatusSchema,
  /** Deliberately not an FK on the model side (F12) — null until the claim is submitted. */
  workflowInstanceId: uuidSchema.nullable(),
  attachmentCount: z.number().int().nonnegative(),
  submittedAt: instantSchema.nullable(),
  /** Set once, at `settleClaim`, by whoever holds `expense:settle` (the `finance` role). Null until then. */
  payoutMode: payoutModeSchema.nullable(),
  payoutReference: z.string().nullable(),
  settledAt: instantSchema.nullable(),
  settledById: uuidSchema.nullable(),
  settledByName: z.string().nullable(),
  /** EXP-P1-9 — set only by `cancelClaim`, on an `approved` claim stopped before it is paid. Null
   * on every other status, and forever on a claim cancelled before this field existed. Any reason
   * given lands on the claim's own comment thread, the same place a send-back reason does — not a
   * separate field here. */
  cancelledAt: instantSchema.nullable(),
  cancelledById: uuidSchema.nullable(),
  cancelledByName: z.string().nullable(),
  /** A snapshot of the category's `glAccountCode` at the moment `settleClaim` wrote these
   * settlement fields — see the model's own comment for why this is never a live join. Null on
   * every unsettled claim, and forever on one settled before its category had a code, or before
   * this field existed at all. */
  glAccountCode: z.string().nullable(),
  /** Multi-currency (2026-09-23) — `amount` converted to the org's base currency at the rate in
   * force when `settleClaim` wrote these fields; a snapshot, same reasoning as `glAccountCode`
   * just above. Null when the claim's own currency already IS the base currency (nothing to
   * convert), the claim isn't settled yet, or no rate existed for it at settlement time — Paid
   * History falls back to `amount` in that last case, since "cannot verify" is not "zero". */
  amountInBaseCurrency: decimalStringSchema.nullable(),
  /** EXP-P2-11 (Keka parity) — how much of `payableAmount` `settleExpenseClaim` decided was taxable
   * income under the category's `taxTreatment`, frozen at settlement — see the model's own comment
   * for why this is a snapshot. Null on every unsettled claim, and forever on one settled before
   * this field existed (read as fully exempt, this module's own behaviour before this feature). */
  taxablePerquisiteAmount: decimalStringSchema.nullable(),
  /** EXP-P2-14 (Keka parity) — a bank bounce/return on THIS claim's own payout leg, reported
   * through `reportClaimPaymentFailure`. Null on a claim that was never marked failed (whether
   * never settled, still settled, or settled before this feature existed). Set only alongside the
   * claim reverting to `approved` — `payoutMode`/`payoutReference`/`settledAt`/`settledById`/
   * `glAccountCode`/`amountInBaseCurrency`/`taxablePerquisiteAmount` are cleared at the same
   * moment (the claim is unsettled again, free for a fresh attempt), which is exactly why
   * `lastFailedPayoutReference` exists — a snapshot of what `payoutReference` WAS, since the live
   * field is about to go back to null. */
  lastPaymentFailedAt: instantSchema.nullable(),
  lastPaymentFailureReason: z.string().nullable(),
  lastPaymentFailedById: uuidSchema.nullable(),
  lastPaymentFailedByName: z.string().nullable(),
  lastFailedPayoutReference: z.string().nullable(),
  /** ADDED 2026-09-17 — which payment batch this claim was grouped into, if any. Set by
   * `createPaymentBatch`, cleared by nothing (a batched claim stays batched even once `paid` —
   * see `ExpensePaymentBatch`'s own header comment on why there's no route back). A claim with
   * this set cannot be settled individually through `settleClaim`; it settles when its batch does. */
  paymentBatchId: uuidSchema.nullable(),
  /** ADDED for TG-5 — the advance this claim is being netted against, if the employee picked one at
   * create/update. Chosen only from their own `disbursed` advances (the route's own check); never
   * set once the claim is settled the other direction round (an advance is never retrofitted onto
   * an already-settled claim). */
  advanceId: uuidSchema.nullable(),
  advanceReference: z.string().nullable(),
  /** How much of THIS claim was covered by the advance above, decided by the server, never by the
   * caller — null forever on a claim with no `advanceId`. EXP-P0-1 (2026-10-06): set the moment
   * the claim is EARMARKED for a payout path (joins a bank payment batch, is reserved onto a
   * payroll run) or is settled directly, whichever comes first — not necessarily only once
   * `status` is `settled`. See `PAYOUT_MODES`'s own `advance` value: when this equals
   * `approvedAmount ?? amount`, `payoutMode` is `advance` and no further money moved; when it is
   * less, the remainder was paid through whatever `payoutMode` the settle actually recorded. */
  advanceAppliedAmount: decimalStringSchema.nullable(),
  /** EXP-P0-6 — what is actually owed/was actually paid: `(approvedAmount ?? amount) minus
   * advanceAppliedAmount`, server-computed, never the caller's to set. Every screen that shows "the
   * amount being paid" (the settlement queue, a batch's lines and total, paid history, the payment
   * slip) must read THIS, not `amount` — the raw claimed figure, which an approver may have reduced
   * and an advance may have partly covered. Equal to `amount` on the common case (no reduction, no
   * advance) so most claims show one unremarkable figure. */
  payableAmount: decimalStringSchema,
  /** R-P8 (Keka gap row 25) — set when this claim is a line item inside a multi-line
   * `expense-multiline-report.contract.ts` report rather than standalone. Set only at
   * `createClaim`; a report's currency and status gate which claims may join it — see that
   * contract's own header for why the grouping is a separate entity, not a reshape of this one. */
  reportId: uuidSchema.nullable(),
  /** R-P10 — an optional, purely-for-reporting tag to the trip this expense belongs to. Never
   * gates or auto-creates anything — see `travel-request.contract.ts`'s own header. */
  travelRequestId: uuidSchema.nullable(),
  /** EXP-P2-12 (Keka parity) — an optional, purely-for-reporting tag to a project/cost centre, same
   * "never gates anything" convention as `travelRequestId` just above. No foreign key (see
   * `ExpenseClaim.projectId`'s own schema comment), so — same as `travelRequestId` itself, which
   * also carries no resolved name here — the caller resolves a display name from its own
   * `GET /lookup/projects` / `GET /lookup/cost-centres` list, already fetched for the picker. */
  projectId: uuidSchema.nullable(),
  costCentreId: uuidSchema.nullable(),
  /** R-P5 (Keka gap row 17) — how much of `amount` an approver actually authorised, when it was
   * less than claimed. Null on a rejected/pending/draft claim, and on an approved one the approver
   * chose not to reduce — every settle path uses `approvedAmount ?? amount` (see
   * `claimPayableAmount` in `expense-payable.ts`), so the two are never both consulted separately. */
  approvedAmount: decimalStringSchema.nullable(),
  /** R-P5 (Keka gap row 8) — a receipt on this claim shares its content hash with a receipt on a
   * DIFFERENT claim (`uploadClaimAttachment`'s own duplicate check, re-run here for display).
   * `false` on every list that doesn't attach it (only `listApprovals`/`getClaim` do) — same "not
   * computed" vs "computed and clean" distinction `activeStep`'s own comment draws for null. */
  hasDuplicateReceipt: z.boolean(),
  /** EXP-P2-8 — a DIFFERENT signal from the one above: this same person has ANOTHER claim with the
   * same amount, currency, expense date and merchant name — the far more common "re-photographed
   * and re-filed the same bill" case, where the receipt image itself differs (or one side has
   * none at all) but the underlying expense is the same one. Scoped to one person's own claims:
   * the same combination across two DIFFERENT people is far more likely a shared expense (a group
   * cab, a team lunch) than a duplicate. No status filter, matching `hasDuplicateReceipt`'s own
   * convention — even two drafts catch a mistake before either is ever submitted. Same "false on
   * every list that doesn't compute this" convention as `hasDuplicateReceipt`. */
  hasPossibleDuplicateClaim: z.boolean(),
  /** R-P5 (Keka gap row 8) — this pending claim currently fails its assigned policy's category
   * rule (`expense-policy-engine.ts`'s `checkCategoryRule`), re-checked at read time rather than
   * only at submit: an admin can tighten a rule after a claim is already pending. `[]` means either
   * compliant, no applicable policy, or not computed for this claim (only `listApprovals`/
   * `getClaim` compute it, and only for a `pending` claim — the flag was never meant to be a
   * historical record on decided claims). Never blocks anything by itself; only `submitClaim`'s own
   * gate does that. */
  policyViolations: z.array(z.string()),
  /**
   * The approval step currently waiting on someone, when there is one — null once decided, for a
   * draft, and on every list that does not attach it (only the lists a person acts from do:
   * `listClaims`, `listApprovals`, `getClaim`). `dueAt` is the step's SLA deadline (null when the
   * definition set none); `escalatedAt` is set once the engine's SLA sweep has already reassigned
   * it. "Overdue" is `dueAt < now && escalatedAt === null`, computed by the screen, not stored.
   */
  activeStep: z
    .object({
      stepNo: z.number().int(),
      dueAt: instantSchema.nullable(),
      escalatedAt: instantSchema.nullable(),
    })
    .nullable(),
  /**
   * Set while a payroll run holds this claim to pay it through salary (`payroll-inputs.ts`'s
   * `includeClaim`). `settleClaim` already refuses such a claim with a 409; this is what lets the
   * Settlement queue SAY so instead of offering a "Mark as paid" that can only fail. Null once the
   * run commits (the claim is then `settled`, payout mode `payroll_add_on`) or releases it.
   */
  payrollReservation: z
    .object({
      runId: uuidSchema,
      periodStart: z.string(),
      periodEnd: z.string(),
      reservedAt: instantSchema,
      /**
       * The run's own pay date — the ONE honest answer to "when do I get this money" that this
       * module can give. Everything else is a guess: a claim headed for a bank transfer goes
       * into a payment batch finance creates ad hoc, with no schedule, no SLA and no cadence
       * anywhere to derive a date from. So a date is shown only once a payroll run actually
       * holds the claim, and nothing is promised before that. Nullable because a run can be
       * created before its pay date is set.
       */
      payDate: z.string().nullable(),
    })
    .nullable(),
  /** R-P4 (Keka parity) — the GST split, computed server-side at submit from `amount`/`gstAmount`
   * by the shared `gst.ts` helper. Null on a claim with no GST, or filed before this phase. */
  billedToGstin: gstinSchema.nullable(),
  placeOfSupply: z.string().nullable(),
  taxableAmount: decimalStringSchema.nullable(),
  gstRate: decimalStringSchema.nullable(),
  cgstAmount: decimalStringSchema.nullable(),
  sgstAmount: decimalStringSchema.nullable(),
  igstAmount: decimalStringSchema.nullable(),
  cessAmount: decimalStringSchema.nullable(),
  itcEligible: z.boolean().nullable(),
  /** R-P4 — the exchange rate and base-currency amount at submit (distinct from
   * `amountInBaseCurrency`, the settlement-time snapshot — see that field's own comment). */
  submitFxRate: decimalStringSchema.nullable(),
  submitBaseAmount: decimalStringSchema.nullable(),
  /** R-P6 (Keka gap row 12) — this claim's answers to its category's `customAttributeDefs`.
   * `{}` means none entered (a category with no custom fields, or all-optional ones). */
  customAttributeValues: expenseCustomAttributeValuesSchema,
  /** R-P6 (Keka gap row 9) — set only when the category's `usageType` is `mileage`; `amount` is
   * computed server-side from this and the assigned policy's `mileageRatePerUnit` at create,
   * update AND submit (a running best-effort figure until submit, then a final snapshot — same
   * "recomputed, never trusted from the client" reasoning as R-P4's GST split). Null otherwise. */
  distanceKm: decimalStringSchema.nullable(),
  /** R-P6 (Keka gap row 9) — set only when the category's `usageType` is `per_diem`; `amount` is
   * `(days inclusive) * perDiemDailyRate`, computed the same way as `distanceKm` above. Null
   * otherwise. */
  perDiemStartDate: dateOnlySchema.nullable(),
  perDiemEndDate: dateOnlySchema.nullable(),
  /** Split-bill (2026-09-28) — `false`/`[]` for the overwhelming majority of claims that never
   * split. Once true, `categoryId`/`categoryName` above are informational only (set to whichever
   * split's category is largest) — every real cap/policy check runs per split, not on this claim
   * as a whole. */
  isSplit: z.boolean(),
  splits: z.array(expenseClaimSplitSchema),
  createdAt: instantSchema,
});
export type ExpenseClaim = z.infer<typeof expenseClaimSchema>;

/**
 * One uploaded receipt — ADDED 2026-09-17, closing REIMBURSEMENT_STATUS.md row 17. The
 * `Attachment` model, its `expenseClaimId` column, and `attachmentCount` on the DTO above all
 * existed already; nothing could ever create a row. Now something can.
 *
 * NEVER CARRIES THE OBJECT KEY. `Attachment.spacesKey` is an internal storage path (a Spaces key
 * in production, a path under `UPLOAD_DIR` in dev) — handing it to a client would leak where bytes
 * live and invite someone to construct their own. The bytes come back through
 * `downloadClaimAttachment` instead, which re-checks permission on every fetch.
 */
export const expenseAttachmentSchema = z.object({
  id: uuidSchema,
  expenseClaimId: uuidSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedById: uuidSchema.nullable(),
  uploadedByName: z.string().nullable(),
  /**
   * Set when this receipt's bytes match one already attached to a DIFFERENT claim in this org —
   * a flag, never a block (see `uploadClaimAttachment`'s own comment on why). Null on every other
   * attachment, including a re-upload of the same file to the SAME claim (not a duplicate of
   * anything — it's the same claim's own receipt).
   */
  duplicateOf: z
    .object({
      claimId: uuidSchema,
      claimReference: z.string().nullable(),
      personName: z.string(),
    })
    .nullable(),
  createdAt: instantSchema,
});
export type ExpenseAttachment = z.infer<typeof expenseAttachmentSchema>;

/**
 * The upload body. Base64 rather than multipart, deliberately: every other route in this contract
 * is JSON end to end, ts-rest types a JSON body natively, and a receipt is small enough that the
 * ~33% base64 overhead costs less than introducing a second request format (and a multipart
 * parser dependency) for one route. `apps/api/src/app.ts` mounts a larger JSON body limit scoped
 * to this path alone, so the global 2 MB guard on every other route is untouched.
 */
export const uploadExpenseAttachmentBodySchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  /** Enforced server-side against a real allow-list — PDF, JPEG, PNG (`storage.ts`). A client can
   * claim any type; the route is what decides. */
  mimeType: z.string().trim().min(1).max(100),
  /** The file itself, base64-encoded, no data: URI prefix. */
  contentBase64: z.string().min(1),
});

/**
 * One row of a claim's approval history — added 2026-09-14 for the Claim Detail audit trail, which
 * had been rendering `mockTimeline` (a fictional 4-stage script, only two of which correspond to
 * anything a real `WorkflowInstance` knows about) since the panel was first built. Deliberately
 * carries RAW facts, not pre-composed UI text or a formatted date — the same separation
 * `expenseClaimStatusSchema` keeps from `StatusPill`'s label mapping: the client decides how a
 * `decision` of `null` with an assigned approver reads ("Awaiting X") versus one with none yet
 * ("Not yet reached"), not this DTO.
 */
export const expenseClaimTimelineStepSchema = z.object({
  stepNo: z.number().int(),
  approverRule: z.string(),
  isParallel: z.boolean(),
  /** Who it is currently waiting on, if this step is active. Null once decided or not yet reached. */
  approverPersonId: uuidSchema.nullable(),
  approverName: z.string().nullable(),
  decision: z.enum(['approved', 'rejected', 'skipped', 'delegated']).nullable(),
  resolvedById: uuidSchema.nullable(),
  resolvedByName: z.string().nullable(),
  resolvedAt: instantSchema.nullable(),
  comment: z.string().nullable(),
  /** The step's SLA deadline, null when its definition set none. Raw fact, like `resolvedAt`. */
  dueAt: instantSchema.nullable(),
  /** When the engine's SLA sweep reassigned this step past its deadline; null if it never did. */
  escalatedAt: instantSchema.nullable(),
});
export type ExpenseClaimTimelineStep = z.infer<typeof expenseClaimTimelineStepSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listExpenseCategoriesQuerySchema = paginationQuerySchema.extend({
  /**
   * Everyone else always sees active-only, regardless of this flag — see the route. Exists for the
   * Admin "Reimbursement Settings" screen, which has to show a deactivated category to let someone
   * reactivate it.
   */
  includeInactive: z.coerce.boolean().optional(),
});

/** Admin-only (`expense.policy:manage`) — the category catalog itself, not a claim against it. */
export const createExpenseCategoryBodySchema = z.object({
  code: z
    .string()
    .trim()
    .toLowerCase()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      'Lowercase letters, digits, and underscores, starting with a letter',
    ),
  name: z.string().trim().min(1).max(100),
  maxAmount: decimalStringSchema.optional(),
  monthlyBudget: decimalStringSchema.optional(),
  requiresReceipt: z.boolean().default(false),
  glAccountCode: z.string().trim().min(1).max(40).optional(),
  usageType: expenseCategoryUsageTypeSchema.default('actual'),
  customAttributeDefs: z.array(expenseCustomAttributeDefSchema).default([]),
  itcEligible: z.boolean().default(true),
  defaultGstRate: decimalStringSchema.optional(),
  sacHsnCode: z.string().trim().min(1).max(20).optional(),
  /** Travel Desk (TD-4.1). Omitted is `other`. */
  kind: expenseCategoryKindSchema.optional(),
  /** Travel Desk (TD-4.1): the currency of `maxAmount` / `monthlyBudget`. Omitted is the org's base. */
  currency: currencySchema.optional(),
  /** FBP F3 — see `expenseCategorySchema.flexiComponentId`. Omitted means no flexi link. */
  flexiComponentId: uuidSchema.nullable().optional(),
  /** EXP-P2-11 (Keka parity). Omitted is `exempt` — this module's own behaviour before this field
   * existed. */
  taxTreatment: expenseCategoryTaxTreatmentSchema.default('exempt'),
  exemptionLimit: decimalStringSchema.optional(),
  exemptionPeriod: expenseExemptionPeriodSchema.optional(),
}).refine(
  (body) =>
    body.taxTreatment === 'exempt_with_limit'
      ? body.exemptionLimit !== undefined && body.exemptionPeriod !== undefined
      : body.exemptionLimit === undefined && body.exemptionPeriod === undefined,
  {
    message:
      "'exempt_with_limit' requires both exemptionLimit and exemptionPeriod; any other taxTreatment must not set either",
    path: ['exemptionLimit'],
  },
);

/** Every field optional — a PATCH, not a replace. `isActive` is how deactivate/reactivate works;
 * there is no separate delete, for the same ledger-integrity reason claims themselves used to be
 * drafts-only to delete (this file's `deleteClaim` doc comment) — a category referenced by past
 * claims must keep existing for their `categoryName` to keep resolving. */
export const updateExpenseCategoryBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  maxAmount: decimalStringSchema.nullable().optional(),
  monthlyBudget: decimalStringSchema.nullable().optional(),
  requiresReceipt: z.boolean().optional(),
  glAccountCode: z.string().trim().min(1).max(40).nullable().optional(),
  isActive: z.boolean().optional(),
  usageType: expenseCategoryUsageTypeSchema.optional(),
  customAttributeDefs: z.array(expenseCustomAttributeDefSchema).optional(),
  itcEligible: z.boolean().optional(),
  defaultGstRate: decimalStringSchema.nullable().optional(),
  sacHsnCode: z.string().trim().min(1).max(20).nullable().optional(),
  /** Travel Desk (TD-4.1). */
  kind: expenseCategoryKindSchema.optional(),
  /** Travel Desk (TD-4.1): null puts the caps back in the org's base currency. */
  currency: currencySchema.nullable().optional(),
  /** FBP F3 — see `expenseCategorySchema.flexiComponentId`. Null clears the link. */
  flexiComponentId: uuidSchema.nullable().optional(),
  /** EXP-P2-11 (Keka parity) — a PATCH, so cross-field consistency is only checked within what THIS
   * call sends: setting `taxTreatment` to `exempt_with_limit` must supply both the other two fields
   * in the same call (the route never guesses a limit); setting it to anything else nulls both out
   * server-side regardless of what else this call sends, since they are meaningless without it. */
  taxTreatment: expenseCategoryTaxTreatmentSchema.optional(),
  exemptionLimit: decimalStringSchema.nullable().optional(),
  exemptionPeriod: expenseExemptionPeriodSchema.nullable().optional(),
}).refine(
  (body) =>
    body.taxTreatment !== 'exempt_with_limit' ||
    (body.exemptionLimit !== undefined && body.exemptionPeriod !== undefined),
  {
    message: "Setting taxTreatment to 'exempt_with_limit' requires exemptionLimit and exemptionPeriod in the same request",
    path: ['exemptionLimit'],
  },
);

/**
 * Org-wide reimbursement settings that don't belong on any single category or policy. Renamed from
 * `ExpensePolicy` (R-P2, 2026-09-26) — that name now belongs to the real, Keka-equivalent policy
 * concept below. `maxAdvanceAmount` (audit-confirmed gap, 2026-09-23) is the only setting today;
 * null means uncapped. An org with no row at all is also uncapped — `getExpenseSettings` never
 * 404s, it returns the all-null shape.
 */
export const expenseSettingsSchema = z.object({
  orgId: uuidSchema,
  maxAdvanceAmount: decimalStringSchema.nullable(),
  /** EXP-P1-12 — how many days after DISBURSEMENT a generic (non-trip) advance is expected to be
   * settled. Null means no window is configured, same "uncapped means null" convention
   * `maxAdvanceAmount` already uses — `disburseAdvance` then leaves `expectedSettlementDate`
   * unset and the overdue reminder never flags it, exactly today's behaviour. A trip advance
   * ignores this entirely; its own settle-by comes from the trip's `TravelPolicy.settleWithinDays`
   * instead (`settleByInstant`'s own comment), set at request time, before disbursement. */
  advanceSettleWithinDays: z.number().int().positive().nullable(),
  /** What every ExpenseExchangeRate converts INTO, and the currency a currency-aware cap/budget
   * comparison treats a same-currency claim as already matching. Never null — see the model's own
   * comment on why "INR" is the honest default rather than an invented neutral one. */
  baseCurrency: currencySchema,
  updatedAt: instantSchema.nullable(),
  updatedById: uuidSchema.nullable(),
  updatedByName: z.string().nullable(),
});
export type ExpenseSettings = z.infer<typeof expenseSettingsSchema>;

export const updateExpenseSettingsBodySchema = z.object({
  maxAdvanceAmount: decimalStringSchema.nullable().optional(),
  advanceSettleWithinDays: z.number().int().positive().nullable().optional(),
  baseCurrency: currencySchema.optional(),
});

/**
 * R-P2 (Keka parity, gap rows 2–3) — a named policy grouping categories and their rules, assigned
 * to a scope. Schema and contract only in this phase: no route resolves "which policy applies to
 * this person" yet (R-P3's engine). `assignments`/`categoryRules` are returned inline because an
 * admin screen always needs both alongside the policy itself — same "small, always-together lists
 * inline" convention `expenseClaimSchema.activeStep` already uses elsewhere in this file.
 */
export const expensePolicyAssignmentSchema = z.object({
  id: uuidSchema,
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  legalEntityId: uuidSchema.nullable(),
  legalEntityName: z.string().nullable(),
  grade: z.string().nullable(),
  employmentType: z.string().nullable(),
  personId: uuidSchema.nullable(),
  personName: z.string().nullable(),
});
export type ExpensePolicyAssignment = z.infer<typeof expensePolicyAssignmentSchema>;

/** Every field optional and, deliberately, no `.refine()` requiring at least one — an assignment
 * with every field empty IS a legitimate state: `resolvePolicyForPerson`'s own contract is that
 * such a row is the org-wide default, matching everyone. Rejecting it here would make that state
 * unreachable through the API. */
export const createExpensePolicyAssignmentBodySchema = z.object({
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  legalEntityId: uuidSchema.optional(),
  grade: z.string().trim().min(1).max(60).optional(),
  employmentType: z.string().trim().min(1).max(40).optional(),
  personId: uuidSchema.optional(),
});

export const expensePolicyCategoryRuleSchema = z.object({
  id: uuidSchema,
  categoryId: uuidSchema,
  categoryName: z.string(),
  periodLimitAmount: decimalStringSchema.nullable(),
  periodLimitPeriod: expensePolicyRulePeriodSchema.nullable(),
  countLimit: z.number().int().nonnegative().nullable(),
  countLimitPeriod: expensePolicyRulePeriodSchema.nullable(),
  claimWindowDays: z.number().int().nonnegative().nullable(),
  receiptRequiredAboveAmount: decimalStringSchema.nullable(),
  noteRequiredAboveAmount: decimalStringSchema.nullable(),
  mileageRatePerUnit: decimalStringSchema.nullable(),
  mileageUnit: expenseMileageUnitSchema.nullable(),
  perDiemDailyRate: decimalStringSchema.nullable(),
  /** Effective-dating (2026-09-28) — read-only, informational. `expensePolicySelect` only ever
   * returns the row CURRENT as of today, so `validTo` here is always null in practice; both fields
   * exist on the DTO so a future "rule history" screen needs no contract change to show past rows. */
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
});
export type ExpensePolicyCategoryRule = z.infer<typeof expensePolicyCategoryRuleSchema>;

export const upsertExpensePolicyCategoryRuleBodySchema = z
  .object({
    categoryId: uuidSchema,
    periodLimitAmount: decimalStringSchema.nullable().optional(),
    periodLimitPeriod: expensePolicyRulePeriodSchema.nullable().optional(),
    countLimit: z.number().int().nonnegative().nullable().optional(),
    countLimitPeriod: expensePolicyRulePeriodSchema.nullable().optional(),
    claimWindowDays: z.number().int().nonnegative().nullable().optional(),
    receiptRequiredAboveAmount: decimalStringSchema.nullable().optional(),
    noteRequiredAboveAmount: decimalStringSchema.nullable().optional(),
    mileageRatePerUnit: decimalStringSchema.nullable().optional(),
    mileageUnit: expenseMileageUnitSchema.nullable().optional(),
    perDiemDailyRate: decimalStringSchema.nullable().optional(),
    /** Effective-dating (2026-09-28) — when this version takes effect. Defaults to today
     * (server-side, the caller's own resolved "today") when omitted — this never rewrites history,
     * it closes whatever row is currently open and starts a new one from this date. */
    effectiveFrom: dateOnlySchema.optional(),
  })
  .refine((body) => (body.periodLimitAmount == null) === (body.periodLimitPeriod == null), {
    message: 'periodLimitAmount and periodLimitPeriod must be set together',
  })
  .refine((body) => (body.countLimit == null) === (body.countLimitPeriod == null), {
    message: 'countLimit and countLimitPeriod must be set together',
  });

export const expensePolicySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  priority: z.number().int(),
  isActive: z.boolean(),
  assignments: z.array(expensePolicyAssignmentSchema),
  categoryRules: z.array(expensePolicyCategoryRuleSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type ExpensePolicy = z.infer<typeof expensePolicySchema>;

export const createExpensePolicyBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional(),
  priority: z.number().int().default(0),
});

export const updateExpensePolicyBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  priority: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

/** One category the caller's own resolved policy has a rule for — enough for the claim form to
 * filter its category picker and show "₹X of ₹Y left this [period]". `periodSpent`/`countUsed`
 * are already-committed usage (pending+approved+settled), same convention `checkCategoryRule`'s
 * server-side gate uses; a fresh claim being typed is not yet counted. */
export const expenseMyPolicyCategorySchema = z.object({
  categoryId: uuidSchema,
  categoryName: z.string(),
  periodLimitAmount: decimalStringSchema.nullable(),
  periodLimitPeriod: expensePolicyRulePeriodSchema.nullable(),
  periodSpent: decimalStringSchema.nullable(),
  countLimit: z.number().int().nullable(),
  countLimitPeriod: expensePolicyRulePeriodSchema.nullable(),
  countUsed: z.number().int().nullable(),
  claimWindowDays: z.number().int().nullable(),
  receiptRequiredAboveAmount: decimalStringSchema.nullable(),
  noteRequiredAboveAmount: decimalStringSchema.nullable(),
  /** R-P6 — the rate the claim form uses to show a live "≈ ₹X" preview as the employee types a
   * distance or a date range, before create/update's own best-effort computation confirms it.
   * Null when this category isn't `mileage`/`per_diem`, or no rate is configured for it. */
  mileageRatePerUnit: decimalStringSchema.nullable(),
  mileageUnit: expenseMileageUnitSchema.nullable(),
  perDiemDailyRate: decimalStringSchema.nullable(),
});
export type ExpenseMyPolicyCategory = z.infer<typeof expenseMyPolicyCategorySchema>;

/** Null `policyId` means no policy applies to the caller — every active category is offered,
 * unrestricted, same as before this feature existed. */
export const expenseMyPolicySummarySchema = z.object({
  policyId: uuidSchema.nullable(),
  categories: z.array(expenseMyPolicyCategorySchema),
});
export type ExpenseMyPolicySummary = z.infer<typeof expenseMyPolicySummarySchema>;

/**
 * Deliberately its own schema, not `decimalStringSchema` — that one permits a leading `-` (wrong
 * for a rate, which is never negative) and stops at 4 decimal places (too coarse for some real
 * currency pairs; the column itself is `Decimal(18, 6)`).
 */
const exchangeRateStringSchema = z
  .string()
  .regex(/^\d{1,12}(\.\d{1,6})?$/, 'Must be a positive decimal with at most 6 places')
  .refine((value) => Number(value) > 0, 'Must be greater than zero');

/** EXP-P2-13 (Keka parity) — `manual` is the default, and the only value any rate entered before
 * this field existed could have (this module calls no live rate service — see
 * `ExchangeRatesCard`'s own on-screen copy). Free text, not an enum: Finance's own vocabulary for
 * whose rate this is, same convention as `glAccountCode`. */
const exchangeRateSourceSchema = z.string().trim().min(1).max(40);

/**
 * One manual, admin-entered exchange rate, EFFECTIVE FROM a date — "1 unit of `currency` equals
 * `rateToBase` units of the org's `baseCurrency`, from `effectiveFrom` onward" (2026-09-23,
 * extended 2026-10-07 for EXP-P2-13: a correction or a rate loaded ahead of time is a NEW row for
 * a new date, never an overwrite of a date that already has one — `GET /exchange-rates` returns
 * every dated row for a currency, oldest first, not only "the current one").
 */
export const expenseExchangeRateSchema = z.object({
  currency: currencySchema,
  rateToBase: exchangeRateStringSchema,
  effectiveFrom: dateOnlySchema,
  source: exchangeRateSourceSchema,
  updatedAt: instantSchema,
  updatedById: uuidSchema.nullable(),
  updatedByName: z.string().nullable(),
});
export type ExpenseExchangeRate = z.infer<typeof expenseExchangeRateSchema>;

export const upsertExpenseExchangeRateBodySchema = z.object({
  currency: currencySchema,
  rateToBase: exchangeRateStringSchema,
  /** Omitted means today — same "an ordinary entry needs no extra thought" convention a
   * non-dated rate had before this field existed. */
  effectiveFrom: dateOnlySchema.optional(),
  /** Omitted means `manual`. */
  source: exchangeRateSourceSchema.optional(),
});

/**
 * A request cap on `ExpenseAdvance` that varies by the RECIPIENT's grade (2026-09-28, Keka parity),
 * on top of `ExpenseSettings.maxAdvanceAmount`'s org-wide one. No row for a grade means uncapped BY
 * GRADE (the org-wide ceiling, if any, still applies) — `maxAmount` is deliberately non-nullable,
 * since a row that exists only to say "uncapped" adds nothing the missing-row convention doesn't
 * already give for free. `grade` is free text, matched exactly like
 * `ExpensePolicyAssignment.grade` — grade is not yet real managed data anywhere in this codebase.
 */
export const expenseAdvanceGradeCeilingSchema = z.object({
  grade: z.string().trim().min(1).max(80),
  maxAmount: positiveDecimalStringSchema,
  currency: currencySchema,
  updatedAt: instantSchema,
  updatedById: uuidSchema.nullable(),
  updatedByName: z.string().nullable(),
});
export type ExpenseAdvanceGradeCeiling = z.infer<typeof expenseAdvanceGradeCeilingSchema>;

export const upsertExpenseAdvanceGradeCeilingBodySchema = z.object({
  grade: z.string().trim().min(1).max(80),
  maxAmount: positiveDecimalStringSchema,
  currency: currencySchema,
});

export const listExpenseClaimsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller's own claims. Anyone else's requires `expense:read` over them. */
  personId: uuidSchema.optional(),
  status: expenseClaimStatusSchema.optional(),
  categoryId: uuidSchema.optional(),
  /** Inclusive bounds on `expenseDate`, for the period picker the designs put in the header. */
  fromDate: dateOnlySchema.optional(),
  toDate: dateOnlySchema.optional(),
  /** Free text over reference, merchant, and business purpose. */
  q: z.string().trim().min(1).max(120).optional(),
});

/**
 * ADDED 2026-09-17 — `listSettlements` took pagination only, so Finance got one flat list of
 * every approved claim in the org with no way to narrow it. `status` is deliberately absent: this
 * queue is `approved` by definition (the route's own `where` clause), never a caller-chosen filter.
 */
export const listExpenseSettlementsQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  fromDate: dateOnlySchema.optional(),
  toDate: dateOnlySchema.optional(),
  q: z.string().trim().min(1).max(120).optional(),
});

/**
 * The paid-claims ledger — the finance view of everything already settled. The Settlement queue
 * above only ever holds claims still awaiting payment, so once a claim is paid it disappears from
 * finance's screens; this is where it stays findable ("I was never paid" a month later).
 *
 * `paidFrom`/`paidTo` filter on the day it was settled, not the day the money was spent — the
 * question finance is asked is "when did you pay this", and `expenseDate` can be weeks earlier.
 * `q` also matches the payout reference and the employee's name, which is how a payment gets found
 * from a bank statement line or a phone call.
 */
export const listPaidClaimsQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  paymentBatchId: uuidSchema.optional(),
  paidFrom: dateOnlySchema.optional(),
  paidTo: dateOnlySchema.optional(),
  q: z.string().trim().min(1).max(120).optional(),
});

/**
 * Creates a DRAFT. Nothing is routed for approval until `submitClaim` is called, which is what
 * makes the designs' "Save as Expense Draft" a real state rather than a local-only convenience.
 *
 * Note what is NOT validated here: whether `expenseDate` is in the future. "Future" depends on the
 * employee's `location.timezone` (invariant 3), which Zod cannot see — an employee in Auckland and
 * one in Mumbai disagree about what day it is for several hours out of every day. That check belongs
 * in the route. So do `maxAmount` and `requiresReceipt`, which need the category row and the
 * attachment count.
 */
export const createExpenseClaimBodySchema = z
  .object({
    /** ADDED 2026-09-28 (Keka parity) — name someone ELSE as the claimant. Absent/self needs only
     * `expense:submit` as today; naming anyone else additionally needs `expense.claim:file_for`
     * over them — the route checks both, this schema only sees the id. */
    personId: uuidSchema.optional(),
    categoryId: uuidSchema,
    expenseDate: dateOnlySchema,
    /** R-P6 — optional here on purpose: a `mileage`/`per_diem` category computes this server-side
     * from `distanceKm`/the per-diem date range, so the client has nothing real to send yet on
     * first save. Required at the route for an `actual` category, same place `maxAmount` and
     * `requiresReceipt` are already enforced (this file's own header comment on what Zod can't see). */
    amount: positiveDecimalStringSchema.optional(),
    currency: currencySchema,
    gstAmount: positiveDecimalStringSchema.optional(),
    merchantGstin: gstinSchema.optional(),
    /** R-P4 — the GSTIN the bill is addressed to. Null/absent falls back to the legal entity's
     * own GSTIN at submit, per `KEKA_VS_REIMBURSEMENT.md` §3, point 2 (needed to decide
     * intra-state vs inter-state, and to check ITC eligibility). */
    billedToGstin: gstinSchema.optional(),
    merchantName: z.string().trim().min(1).max(200),
    billNumber: z.string().trim().max(60).optional(),
    paidMode: paidModeSchema.optional(),
    /**
     * The designs make this a required field with a real example behind it ("team celebration and
     * client onboarding dinner, 5 members"), and they are right to: a claim with no stated purpose
     * is exactly the one an approver has to send back.
     */
    businessPurpose: z.string().trim().min(1).max(1000),
    /** Optional — which of the employee's own `disbursed` advances this claim nets against at
     * settlement. The route re-checks it is theirs and still open; a Zod schema only sees the id. */
    advanceId: uuidSchema.optional(),
    /** R-P6 — this claim's answers to its category's `customAttributeDefs`. Which keys are
     * actually required depends on the category, so validated at the route, not here. */
    customAttributeValues: expenseCustomAttributeValuesSchema.optional(),
    /** R-P6 — only meaningful (and only ever used) when the category's `usageType` is `mileage`. */
    distanceKm: positiveDecimalStringSchema.optional(),
    /** R-P6 — only meaningful when the category's `usageType` is `per_diem`; both required together
     * to mean anything, checked at the route where it can also confirm the category actually IS
     * `per_diem` (a Zod `.refine()` here cannot see the category row). */
    perDiemStartDate: dateOnlySchema.optional(),
    perDiemEndDate: dateOnlySchema.optional(),
    /** R-P8 — add this claim as a line to one of the caller's own DRAFT multi-line reports instead
     * of leaving it standalone. The route re-checks ownership, draft status, and that the claim's
     * `currency` matches the report's own — a Zod schema only sees the id. */
    reportId: uuidSchema.optional(),
    /** R-P10 — optional, purely-for-reporting tag to a trip; the route only checks it exists and
     * belongs to the caller, nothing more (never gates the claim itself). */
    travelRequestId: uuidSchema.optional(),
    /** EXP-P2-12 (Keka parity) — optional, purely-for-reporting tag to a project/cost centre, same
     * "never gates the claim" reasoning as `travelRequestId` just above. The route only checks each
     * exists, is active, and belongs to the org — see `ExpenseClaim.projectId`'s own schema comment
     * for why there is no real foreign key. */
    projectId: uuidSchema.optional(),
    costCentreId: uuidSchema.optional(),
    /** Split-bill (2026-09-28) — divide this claim's amount across 2+ categories instead of one.
     * Absent/undefined means an ordinary, unsplit claim (the overwhelming majority) — nothing else
     * changes for that case. What Zod cannot see (category existence/active/`usageType === 'actual'`
     * for every split, no duplicate categoryId, and that the splits sum EXACTLY to `amount`) is the
     * route's job, same as every other business rule this file's header already defers there. */
    splits: z.array(expenseClaimSplitInputSchema).min(2).optional(),
  })
  .refine((body) => body.gstAmount === undefined || body.merchantGstin !== undefined, {
    message:
      'merchantGstin is required when gstAmount is given — GST is only claimable against one',
    path: ['merchantGstin'],
  })
  .refine((body) => (body.perDiemStartDate === undefined) === (body.perDiemEndDate === undefined), {
    message: 'perDiemStartDate and perDiemEndDate must be given together',
    path: ['perDiemEndDate'],
  })
  .refine(
    (body) =>
      body.splits === undefined ||
      new Set(body.splits.map((s) => s.categoryId)).size === body.splits.length,
    { message: 'A claim cannot have two splits for the same category', path: ['splits'] },
  );

/**
 * Every field from `createExpenseClaimBodySchema`, but a PATCH: all optional, since editing a draft
 * means changing SOME of it, not resupplying the whole thing. The same GST/GSTIN pairing rule
 * applies, but only against fields present in THIS request — it cannot see the row's existing
 * values (this is a Zod schema, not a DB read), so setting `gstAmount` without `merchantGstin`
 * already on the row is caught here, but the reverse (the row already has `merchantGstin`, this
 * request only touches `gstAmount`) is the route's job, same as every other business rule the
 * request body alone cannot see (`createExpenseClaimBodySchema`'s own header comment).
 *
 * ONLY EVER APPLIES TO A DRAFT. `updateClaim`'s own route enforces that — nothing about what was
 * spent, when, or on what should be editable after `submitClaim` has started a real approval
 * workflow against the figures as submitted.
 */
export const updateExpenseClaimBodySchema = z
  .object({
    categoryId: uuidSchema.optional(),
    expenseDate: dateOnlySchema.optional(),
    amount: positiveDecimalStringSchema.optional(),
    currency: currencySchema.optional(),
    gstAmount: positiveDecimalStringSchema.optional(),
    merchantGstin: gstinSchema.optional(),
    billedToGstin: gstinSchema.optional(),
    merchantName: z.string().trim().min(1).max(200).optional(),
    billNumber: z.string().trim().max(60).optional(),
    paidMode: paidModeSchema.optional(),
    businessPurpose: z.string().trim().min(1).max(1000).optional(),
    /** `null` clears a previously-picked advance; omitted leaves it as it was. */
    advanceId: uuidSchema.nullable().optional(),
    customAttributeValues: expenseCustomAttributeValuesSchema.optional(),
    distanceKm: positiveDecimalStringSchema.nullable().optional(),
    perDiemStartDate: dateOnlySchema.nullable().optional(),
    perDiemEndDate: dateOnlySchema.nullable().optional(),
    /** EXP-P2-12 — `null` clears a previously-picked tag; omitted leaves it as it was, same
     * convention as `advanceId` above. */
    projectId: uuidSchema.nullable().optional(),
    costCentreId: uuidSchema.nullable().optional(),
    /**
     * Take this line OUT of the report it belongs to, leaving the expense itself alone as an
     * ordinary standalone draft. `null` is the only accepted value, deliberately: a report is
     * chosen once at creation (`createClaim.reportId`, which validates the report is the
     * caller's own and still a draft, and locks the line to its currency), and re-parenting a
     * line into a DIFFERENT report would have to repeat all of that here. Detaching needs none
     * of it — the line simply stops being part of anything.
     *
     * Deleting the claim was the only way to shorten a report before this, which threw the
     * expense away with it.
     */
    reportId: z.null().optional(),
    /** Split-bill — `undefined` leaves the split set untouched, `null` removes every split
     * (reverting to a plain single-category claim), an array REPLACES the whole set (simplest
     * correct semantics — no per-split PATCH-of-a-PATCH). */
    splits: z.array(expenseClaimSplitInputSchema).min(2).nullable().optional(),
  })
  .refine((body) => body.gstAmount === undefined || body.merchantGstin !== undefined, {
    message: 'merchantGstin is required when gstAmount is given in the same edit',
    path: ['merchantGstin'],
  })
  .refine(
    (body) =>
      body.splits == null ||
      new Set(body.splits.map((s) => s.categoryId)).size === body.splits.length,
    { message: 'A claim cannot have two splits for the same category', path: ['splits'] },
  );

/** Mirrors `decideLeaveRequestBodySchema` exactly — same decision shape, same optional note, plus
 * R-P5's `approvedAmount` (Keka gap row 17: approve a lower amount than claimed). Meaningless on a
 * reject — `approveClaim`'s own handler is what actually enforces `0 < approvedAmount <= amount`,
 * since a Zod schema alone cannot see the row it's being applied to. */
export const decideExpenseClaimBodySchema = z.object({
  comment: z.string().max(500).optional(),
  approvedAmount: decimalStringSchema.optional(),
});

/** R-P5 (Keka gap row 18) — "send back for clarification", not a rejection: `comment` is required
 * (a REQUEST for changes with no reason attached is not one), and unlike `decideExpenseClaimBodySchema`
 * this never carries `approvedAmount` — the claim is going back to be edited, not settled. */
export const sendClaimBackBodySchema = z.object({
  comment: z.string().trim().min(1).max(500),
});

/**
 * ADDED 2026-09-17 (row 16) — deciding a queue of claims in one action instead of one at a time.
 * `max(100)` so one request cannot become an unbounded serial walk through the whole org.
 */
export const bulkDecideExpenseClaimsBodySchema = z.object({
  expenseClaimIds: z.array(uuidSchema).min(1).max(100),
  decision: z.enum(['approved', 'rejected']),
  comment: z.string().max(500).optional(),
});

/**
 * PER-CLAIM OUTCOMES, NOT ALL-OR-NOTHING. Each claim is decided independently and a failure on one
 * says nothing about the rest — the realistic case is a queue where someone else already decided
 * one of them a moment ago, and failing the other ninety-nine over that would be worse than
 * useless. The caller gets back exactly which succeeded and why each failure failed, so the UI can
 * say "94 approved, 6 couldn't be" rather than "something went wrong".
 */
export const bulkDecideExpenseClaimsResultSchema = z.object({
  succeeded: z.array(expenseClaimSchema),
  failed: z.array(
    z.object({
      expenseClaimId: uuidSchema,
      message: z.string(),
    }),
  ),
});

/**
 * ADDED 2026-09-24 — the same draft-only, self-only delete `deleteClaim` performs, over a
 * selection. Same per-claim-outcome shape as bulk decide, for the same reason: one claim that is no
 * longer a draft must not fail the other forty-nine.
 */
export const bulkDeleteExpenseClaimsBodySchema = z.object({
  expenseClaimIds: z.array(uuidSchema).min(1).max(100),
});

export const bulkDeleteExpenseClaimsResultSchema = z.object({
  deletedIds: z.array(uuidSchema),
  failed: z.array(
    z.object({
      expenseClaimId: uuidSchema,
      message: z.string(),
    }),
  ),
});

/**
 * ADDED 2026-09-24 — an approver's own track record: every claim step they decided. Defaults to
 * the caller; anyone else's history needs `workflow:manage` (HR admin, org owner) — the same
 * population that can already see and override every claim org-wide.
 */
export const listExpenseDecisionsQuerySchema = paginationQuerySchema.extend({
  approverPersonId: uuidSchema.optional(),
  decision: z.enum(['approved', 'rejected']).optional(),
});

export const expenseDecisionSchema = z.object({
  /** The WorkflowStep id — unique per decision, so a claim decided twice (a reopen) lists twice. */
  id: uuidSchema,
  claim: expenseClaimSchema,
  approverPersonId: uuidSchema,
  approverName: z.string().nullable(),
  stepNo: z.number().int(),
  /** `skipped` here means a rejection later overturned by a reopen — see the timeline's own note. */
  decision: z.enum(['approved', 'rejected', 'skipped']),
  decidedAt: instantSchema,
  comment: z.string().nullable(),
});
export type ExpenseDecision = z.infer<typeof expenseDecisionSchema>;

/** ADDED 2026-09-17 — the claimant pulling their own claim back before anyone decides it. */
export const withdrawClaimBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

/**
 * EXP-P1-9 — stopping an ALREADY-APPROVED claim before any money moves. Distinct from
 * `withdrawClaimBodySchema` only in which statuses the route accepts (see `cancelClaim`'s own
 * contract entry) — same shape otherwise, same "an optional note, not a workflow decision" reasoning.
 */
export const cancelClaimBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

/**
 * The finance action AFTER approval: recording that the money actually moved. Distinct from
 * `decideExpenseClaimBodySchema` because settling is not a workflow decision — the `WorkflowInstance`
 * is already closed as `approved` by the time this runs (this module's header comment; `@hrms/workflow`
 * has no opinion on what happens to the money afterward).
 *
 * ADVANCE-AWARE (TG-5), BUT NOT HERE — the route decides, never this schema. A claim carrying
 * `advanceId` needs its advance's CURRENT `outstandingAmount` to know how much of it is covered,
 * and a Zod body schema cannot read that row. `settleClaim` computes the applied amount itself and
 * refuses a `payoutMode` other than `advance` when that covers the claim in full — see the route's
 * own comment, not this one.
 */
export const settleClaimBodySchema = z.object({
  payoutMode: payoutModeSchema,
  payoutReference: z.string().trim().max(120).optional(),
  note: z.string().trim().max(500).optional(),
});

/** EXP-P2-14 (Keka parity) — a required reason (wrong account, account closed, …), so Paid
 * History/a reconciliation view always has something to show for why a settled claim is back to
 * `approved` rather than an unexplained status flip. */
export const reportClaimPaymentFailureBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

// ---------------------------------------------------------------------------
// Advances — TG-5 (REIMBURSEMENT_PLAN.md), the task group the first slice left out on purpose
//
// A trip advance: money given to the employee BEFORE they spend it, so "here's ₹15,000 for your
// trip" is a real, tracked fact rather than a conversation. Two lifecycles on ONE row, same split
// `ExpenseClaim` already makes for the same reason: `status` up to `approved`/`rejected`/`withdrawn`
// is the REQUEST — a workflow decision, authorising the money, not moving it. `disbursed` is the one
// finance action that actually pays it out. `settled` is never chosen by a caller; it is reached
// automatically the moment real claims have netted `outstandingAmount` down to zero. `written_off`
// is finance closing one manually, with a mandatory reason, same as `PayrollLoan`'s own write-off.
//
// APPROVAL REUSES THE SAME SHAPE AS A CLAIM: goes through `@hrms/workflow` under its own
// `expense_advance` entity type (a `WorkflowInstance` is unique per `(orgId, entityType, entityId)`
// — it cannot share `expense_claim`'s), `approveAdvance`/`rejectAdvance` declare `routeMeta(null)`
// exactly like `approveClaim`/`rejectClaim` because the handler checks workflow-step eligibility
// itself, and `expense:approve` (not a new permission) gates whether a manager sees the queue at
// all — a manager who can see and act on claim approvals sees and acts on advance ones too.
// ---------------------------------------------------------------------------

export const expenseAdvanceSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** Allocated at submit, e.g. ADV-2026-00001 — same `allocateReference` helper `CLM-`/`PAY-`
   * references use. Null on a draft, same reasoning as `ExpenseClaim.reference`. */
  reference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  /** The trip or reason this advance is for — shown everywhere a claim's `businessPurpose` is. */
  purpose: z.string(),
  currency: currencySchema,
  requestedAmount: decimalStringSchema,
  status: expenseAdvanceStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  submittedAt: instantSchema.nullable(),
  /** Set once, at `disburseAdvance`, to `requestedAmount`; decremented by `settleClaim` as real
   * claims net against it. Null until disbursed — an approved-but-not-yet-paid advance has no
   * balance to speak of yet. */
  outstandingAmount: decimalStringSchema.nullable(),
  disbursedAt: instantSchema.nullable(),
  disbursedById: uuidSchema.nullable(),
  disbursedByName: z.string().nullable(),
  /** EXP-P1-12 — when this advance is expected to be settled; read by the daily overdue reminder.
   * A trip advance gets this at REQUEST time (trip end + the travel policy's own settle window,
   * `settleByInstant`'s own comment); a generic advance only gets one at DISBURSEMENT, and only if
   * the org has `ExpenseSettings.advanceSettleWithinDays` configured — null otherwise, same
   * "uncapped/unconfigured means null, never flagged" convention every other optional limit here
   * uses. */
  expectedSettlementDate: instantSchema.nullable(),
  payoutMode: payoutModeSchema.nullable(),
  payoutReference: z.string().nullable(),
  closedAt: instantSchema.nullable(),
  closedById: uuidSchema.nullable(),
  closedByName: z.string().nullable(),
  closeReason: z.string().nullable(),
  /** R-P10 — an optional, purely-for-reporting tag to the trip this advance is for. Never gates
   * or auto-creates anything — see `travel-request.contract.ts`'s own header. */
  travelRequestId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type ExpenseAdvance = z.infer<typeof expenseAdvanceSchema>;

export const createExpenseAdvanceBodySchema = z.object({
  purpose: z.string().trim().min(1).max(500),
  currency: currencySchema,
  requestedAmount: positiveDecimalStringSchema,
  /**
   * ADDED 2026-09-24 — omitted or equal to the caller's own id: unchanged self-request behaviour,
   * `expense.advance:request` alone still covers it. Any OTHER id requires `expense.advance:issue`
   * over that person (a manager for their own report, HR for anyone) — the route refuses
   * otherwise. The request still goes through the normal approval workflow afterwards; this only
   * changes who may type it in.
   */
  personId: uuidSchema.optional(),
  /** R-P10 — optional, purely-for-reporting tag to a trip; the route only checks it exists and
   * belongs to the requester, nothing more. */
  travelRequestId: uuidSchema.optional(),
});

/** A PATCH, draft-only, same reasoning as `updateExpenseClaimBodySchema`. */
export const updateExpenseAdvanceBodySchema = z.object({
  purpose: z.string().trim().min(1).max(500).optional(),
  currency: currencySchema.optional(),
  requestedAmount: positiveDecimalStringSchema.optional(),
  travelRequestId: uuidSchema.nullable().optional(),
});

export const decideExpenseAdvanceBodySchema = z.object({
  comment: z.string().max(500).optional(),
});

/**
 * EXP-P2-9 — the same "a queue decided in one action" `bulkDecideExpenseClaimsBodySchema` already
 * has, for advances (vs Keka's own bulk actions on reports/advances — the review's own line:
 * "Claims only. Advances have no detail view"). Same max(100) bound, same reasoning.
 */
export const bulkDecideExpenseAdvancesBodySchema = z.object({
  expenseAdvanceIds: z.array(uuidSchema).min(1).max(100),
  decision: z.enum(['approved', 'rejected']),
  comment: z.string().max(500).optional(),
});

/** Same per-advance-outcome shape as `bulkDecideExpenseClaimsResultSchema`, same reasoning. */
export const bulkDecideExpenseAdvancesResultSchema = z.object({
  succeeded: z.array(expenseAdvanceSchema),
  failed: z.array(
    z.object({
      expenseAdvanceId: uuidSchema,
      message: z.string(),
    }),
  ),
});

export const withdrawAdvanceBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

/** Mirrors `settleClaimBodySchema`: the one finance action that pays the advance out for real. */
export const disburseAdvanceBodySchema = z.object({
  payoutMode: payoutModeSchema,
  payoutReference: z.string().trim().max(120).optional(),
});

/** Mandatory reason, same as `closeLoan` (packages/shared/src/contracts/payroll-loans.contract.ts)
 * — a write-off is money the company is choosing to absorb, and that choice earns a paper trail. */
export const closeAdvanceBodySchema = z.object({
  reason: z.string().trim().min(10).max(1000),
});

export const listExpenseAdvancesQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  status: expenseAdvanceStatusSchema.optional(),
  /** Narrows to advances that can still absorb a claim — `disbursed` with `outstandingAmount > 0`
   * — for the picker on the claim form. Checked in the route, not expressible as a plain filter. */
  openOnly: z.coerce.boolean().optional(),
  /** Mirrors `listApprovals`'s own eligibility lookup, not a plain filter: every advance with a
   * live `WorkflowStep` assigned to the caller (or, holding `workflow:manage`, every advance
   * org-wide, minus the caller's own — same self-exclusion `listApprovals` applies, since `decide()`
   * refuses self-approval unconditionally regardless of what a list shows). Ignores `personId` when
   * set — "awaiting my decision" is never scoped to one other person's history. */
  awaitingMyDecision: z.coerce.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Payment batches — ADDED 2026-09-17
//
// Settlement above records that a payment happened; it never moves money or produces anything a
// bank accepts. A batch groups several APPROVED claims, generates one NEFT/RTGS upload file from
// them, and settles the whole group together once finance confirms the bank actually paid it.
//
// SCOPED TO ONE LEGAL ENTITY, ONE CURRENCY (invariant 4, invariant 2) — a bank file uploads to one
// bank account, which belongs to one legal entity in one currency. `createPaymentBatch` enforces
// this at insert, not here: a Zod schema sees only the request body, never the claims themselves.
// ---------------------------------------------------------------------------

export const expensePaymentBatchSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  /** Allocated at creation, e.g. PAY-2026-00001 — never null, unlike a claim's own `reference`; a
   * payment batch is never a discardable draft the way a claim is before its first submit. */
  reference: z.string(),
  currency: currencySchema,
  /** The sum of every claim's `amount` in the batch, denormalised at creation — see the route. */
  totalAmount: decimalStringSchema,
  claimCount: z.number().int().nonnegative(),
  status: expensePaymentBatchStatusSchema,
  /** ADDED 2026-09-17 — which bank's own bulk NEFT/RTGS template `generateBankFile` writes to.
   * Chosen once at creation, reused on every regeneration (see the enum's own comment on why). */
  bankFormat: bankFileFormatSchema,
  /** True once `generateBankFile` has produced the NEFT/RTGS file — never the object key itself,
   * which is an internal Spaces path, not something a client needs. */
  bankFileGenerated: z.boolean(),
  generatedAt: instantSchema.nullable(),
  paidAt: instantSchema.nullable(),
  createdById: uuidSchema,
  createdByName: z.string(),
  createdAt: instantSchema,
});
export type ExpensePaymentBatch = z.infer<typeof expensePaymentBatchSchema>;

export const listExpensePaymentBatchesQuerySchema = paginationQuerySchema.extend({
  status: expensePaymentBatchStatusSchema.optional(),
});

/**
 * Every claim must be `approved` and not already in another batch — checked in the route, inside
 * the transaction, not here: a Zod schema cannot see the claims' current rows, only their ids.
 * `min(1)` is the only shape-level rule this body can enforce.
 *
 * `bankFormat` is REQUIRED, not defaulted — a batch is always paid through one specific bank
 * account, and picking the wrong template silently produces a file that bank will not accept
 * (this file's own header comment on the payment story before this). Forcing an explicit choice
 * here is cheaper than a wrong default nobody notices until the upload fails.
 */
export const createExpensePaymentBatchBodySchema = z.object({
  claimIds: z.array(uuidSchema).min(1).max(500),
  bankFormat: bankFileFormatSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const expenseContract = c.router(
  {
    /**
     * ADDED 2026-09-14 — the New Claim panel hand-copied these six rows into a frontend constant
     * when this contract had no route for them yet (see NewClaimPanel.tsx's own header comment on
     * why that was an honest stopgap, not an oversight). Same shape as `leave.listTypes`: reused
     * `paginationQuerySchema` directly, no extra filter — there is nothing to filter a handful of
     * categories by yet.
     */
    listCategories: {
      method: 'GET',
      path: '/categories',
      query: listExpenseCategoriesQuerySchema,
      responses: {
        200: paginatedSchema(expenseCategorySchema),
        401: errorBodySchema,
      },
      summary: 'List active expense categories for the org',
      // FIXED 2026-09-22 — was `expense:submit`, which 403'd a read-only holder of `expense:read`
      // (e.g. an auditor) on a plain list call, not just a hidden UI button. This route only reads;
      // it never gated submission itself, so the narrower read permission is the correct one.
      metadata: routeMeta('expense:read'),
    },

    /**
     * ADDED 2026-09-14 for the Admin "Reimbursement Settings" screen — the category catalog had a
     * real DB table and a real read route since Step 17/2026-09-14's `listCategories`, but no way
     * to add a new one short of a `seed.ts` edit and a re-seed. Requires `expense.policy:manage`,
     * which only `finance` holds (roles.ts) — the same population as Settlement.
     */
    createCategory: {
      method: 'POST',
      path: '/categories',
      headers: idempotencyHeadersSchema,
      body: createExpenseCategoryBodySchema,
      responses: {
        201: expenseCategorySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a new expense category',
      metadata: routeMeta('expense.policy:manage'),
    },

    updateCategory: {
      method: 'PATCH',
      path: '/categories/:categoryId',
      body: updateExpenseCategoryBodySchema,
      responses: {
        200: expenseCategorySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update an expense category, including activate/deactivate',
      metadata: routeMeta('expense.policy:manage'),
    },

    getExpenseSettings: {
      method: 'GET',
      path: '/settings',
      responses: {
        200: expenseSettingsSchema,
        401: errorBodySchema,
      },
      summary: 'Org-wide reimbursement settings — currently just the advance request ceiling',
      metadata: routeMeta('expense.policy:read'),
    },

    updateExpenseSettings: {
      method: 'PATCH',
      path: '/settings',
      body: updateExpenseSettingsBodySchema,
      responses: {
        200: expenseSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Set the org-wide advance request ceiling and base currency',
      metadata: routeMeta('expense.policy:manage'),
    },

    // --- Policies (R-P3, Keka parity) -----------------------------------------------------

    listPolicies: {
      method: 'GET',
      path: '/policies',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(expensePolicySchema),
        401: errorBodySchema,
      },
      summary: 'List reimbursement policies',
      metadata: routeMeta('expense.policy:read'),
    },

    getPolicy: {
      method: 'GET',
      path: '/policies/:policyId',
      pathParams: z.object({ policyId: uuidSchema }),
      responses: {
        200: expensePolicySchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one reimbursement policy, with its assignments and category rules',
      metadata: routeMeta('expense.policy:read'),
    },

    createPolicy: {
      method: 'POST',
      path: '/policies',
      headers: idempotencyHeadersSchema,
      body: createExpensePolicyBodySchema,
      responses: {
        201: expensePolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a reimbursement policy',
      metadata: routeMeta('expense.policy:manage'),
    },

    updatePolicy: {
      method: 'PATCH',
      path: '/policies/:policyId',
      pathParams: z.object({ policyId: uuidSchema }),
      body: updateExpensePolicyBodySchema,
      responses: {
        200: expensePolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Rename, reprioritise, or activate/deactivate a policy',
      metadata: routeMeta('expense.policy:manage'),
    },

    createPolicyAssignment: {
      method: 'POST',
      path: '/policies/:policyId/assignments',
      pathParams: z.object({ policyId: uuidSchema }),
      body: createExpensePolicyAssignmentBodySchema,
      responses: {
        201: expensePolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Add a scope (department, location, grade, person, ...) this policy applies to',
      metadata: routeMeta('expense.policy:manage'),
    },

    deletePolicyAssignment: {
      method: 'DELETE',
      path: '/policies/:policyId/assignments/:assignmentId',
      pathParams: z.object({ policyId: uuidSchema, assignmentId: uuidSchema }),
      body: z.object({}).optional(),
      responses: {
        200: expensePolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove one of this policy\'s assignments',
      metadata: routeMeta('expense.policy:manage'),
    },

    upsertPolicyCategoryRule: {
      method: 'PUT',
      path: '/policies/:policyId/category-rules',
      pathParams: z.object({ policyId: uuidSchema }),
      body: upsertExpensePolicyCategoryRuleBodySchema,
      responses: {
        200: expensePolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Set (or replace) the rule for one category under this policy',
      metadata: routeMeta('expense.policy:manage'),
    },

    deletePolicyCategoryRule: {
      method: 'DELETE',
      path: '/policies/:policyId/category-rules/:categoryId',
      pathParams: z.object({ policyId: uuidSchema, categoryId: uuidSchema }),
      body: z.object({}).optional(),
      responses: {
        200: expensePolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove a category from this policy — the category is simply not offered to anyone assigned it',
      metadata: routeMeta('expense.policy:manage'),
    },

    myPolicySummary: {
      method: 'GET',
      path: '/my-policy',
      responses: {
        200: expenseMyPolicySummarySchema,
        401: errorBodySchema,
      },
      summary:
        'The caller\'s own resolved policy (if any) and each of its categories\' remaining limit this period — the claim form\'s "₹X of ₹Y left" and category filter',
      metadata: routeMeta('expense:submit'),
    },

    listExchangeRates: {
      method: 'GET',
      path: '/exchange-rates',
      responses: {
        200: paginatedSchema(expenseExchangeRateSchema),
        401: errorBodySchema,
      },
      summary:
        'Every dated exchange-rate entry for this org (EXP-P2-13: full history, not only the current one), newest first',
      metadata: routeMeta('expense.policy:read'),
    },

    upsertExchangeRate: {
      method: 'PUT',
      path: '/exchange-rates',
      body: upsertExpenseExchangeRateBodySchema,
      responses: {
        200: expenseExchangeRateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Add or correct this org's rate for one currency, effective from a date — idempotent per (currency, effectiveFrom)",
      metadata: routeMeta('expense.policy:manage'),
    },

    listAdvanceGradeCeilings: {
      method: 'GET',
      path: '/advance-grade-ceilings',
      responses: {
        200: paginatedSchema(expenseAdvanceGradeCeilingSchema),
        401: errorBodySchema,
      },
      summary: 'Every per-grade advance request ceiling configured for this org',
      metadata: routeMeta('expense.policy:read'),
    },

    upsertAdvanceGradeCeiling: {
      method: 'PUT',
      path: '/advance-grade-ceilings',
      body: upsertExpenseAdvanceGradeCeilingBodySchema,
      responses: {
        200: expenseAdvanceGradeCeilingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Set (or replace) the advance ceiling for one grade — idempotent per grade',
      metadata: routeMeta('expense.policy:manage'),
    },

    deleteAdvanceGradeCeiling: {
      method: 'DELETE',
      path: '/advance-grade-ceilings/:grade',
      pathParams: z.object({ grade: z.string().trim().min(1).max(80) }),
      body: z.object({}).optional(),
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove a grade\'s advance ceiling — that grade goes back to only the org-wide limit',
      metadata: routeMeta('expense.policy:manage'),
    },

    listClaims: {
      method: 'GET',
      path: '/claims',
      query: listExpenseClaimsQuerySchema,
      responses: {
        200: paginatedSchema(expenseClaimSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List expense claims in the callers visible scope',
      metadata: routeMeta('expense:read'),
    },

    /**
     * The "Team approvals" inbox (plan finding F12, closed 2026-09-12) — every claim with a
     * `WorkflowStep` currently awaiting THIS caller's decision, across everyone they approve for,
     * not just their own reports' claims by name. `WorkflowStep` already carries an
     * `[approverPersonId, decision]` index for exactly this query — nothing new needed on the
     * schema side, just the route nobody had wired yet.
     *
     * FOR SOMEONE WITH `workflow:manage` (org-wide), ALSO INCLUDES `rejected` claims (2026-09-14) —
     * `reopen()`'s override exists specifically so they can overturn a rejection, but a rejected
     * claim has no active step, so it was invisible everywhere until this. The client tells the two
     * apart by `status` and offers only "override & approve" for a `rejected` row, never "reject".
     */
    listApprovals: {
      method: 'GET',
      path: '/approvals',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(expenseClaimSchema),
        401: errorBodySchema,
      },
      summary: "List expense claims with a step currently awaiting the caller's decision",
      metadata: routeMeta(null),
    },

    /**
     * The Settlement queue: every `approved` claim org-wide, for whoever holds `expense:settle`
     * (the `finance` role) — same org-wide-vs-assignee split as `listApprovals` above, except there
     * is no per-assignee case here, since settling was never an approval step to begin with.
     */
    listSettlements: {
      method: 'GET',
      path: '/settlements',
      query: listExpenseSettlementsQuerySchema,
      responses: {
        200: paginatedSchema(expenseClaimSchema),
        401: errorBodySchema,
      },
      summary: 'List approved claims awaiting settlement',
      metadata: routeMeta(null),
    },

    listPaidClaims: {
      method: 'GET',
      path: '/paid',
      query: listPaidClaimsQuerySchema,
      responses: {
        200: paginatedSchema(expenseClaimSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List settled claims, newest payment first — the finance payment history',
      metadata: routeMeta('expense:settle'),
    },

    getClaim: {
      method: 'GET',
      path: '/claims/:expenseClaimId',
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one expense claim',
      metadata: routeMeta('expense:read'),
    },

    /**
     * ADDED 2026-09-14 — closes the last mock on the Claim Detail panel. Never paginated: this is
     * one workflow instance's own steps, always a small, bounded number, not a list that grows
     * independently of the claim it belongs to.
     */
    getClaimTimeline: {
      method: 'GET',
      path: '/claims/:expenseClaimId/timeline',
      responses: {
        200: z.object({ items: z.array(expenseClaimTimelineStepSchema) }),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Get a claim's approval step history",
      metadata: routeMeta('expense:read'),
    },

    /**
     * ADDED 2026-09-17 (row 17) — real receipt upload, closing the gap where the web file picker
     * was cosmetic and `attachmentCount` could only ever be 0.
     *
     * DRAFT-ONLY, SELF-ONLY. A receipt is part of what an approver is being asked to judge, so it
     * has to be attached before submit — the same boundary `updateClaim` already draws around
     * everything else about a claim's content. This is also exactly what makes `requiresReceipt`
     * enforceable at submit: the attachment must already exist by then.
     */
    uploadClaimAttachment: {
      method: 'POST',
      path: '/claims/:expenseClaimId/attachments',
      headers: idempotencyHeadersSchema,
      body: uploadExpenseAttachmentBodySchema,
      responses: {
        201: expenseAttachmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not a draft — a submitted claim's evidence is fixed at the moment it was submitted. */
        409: errorBodySchema,
        /** Rejected type, empty/invalid base64, or over the size limit. */
        422: errorBodySchema,
      },
      summary: 'Attach a receipt to a draft claim',
      // ai-hidden because the REQUEST is unsatisfiable by a model, not because the response is
      // sensitive: the body carries `contentBase64`, and a derived tool would ask the model to
      // emit an entire file as base64. It cannot — it would hallucinate bytes or spend its whole
      // output budget trying, then fail validation. Found by
      // `contract-tools.file-upload.test.ts`, which now fails on any route that does this.
      metadata: routeMeta('expense:submit', { ai: 'hidden' }),
    },

    /** Anyone who can READ the claim can list its receipts — an approver deciding a claim needs to
     * see the evidence, which is the entire point of requiring one. */
    listClaimAttachments: {
      method: 'GET',
      path: '/claims/:expenseClaimId/attachments',
      responses: {
        200: z.object({ items: z.array(expenseAttachmentSchema) }),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List a claim's receipts",
      metadata: routeMeta('expense:read'),
    },

    /**
     * The bytes, base64 again (same reasoning as upload) so the client can build a Blob and either
     * preview or save it. Permission is re-checked here, not assumed from the list call — this is
     * the route that actually hands over file contents.
     */
    downloadClaimAttachment: {
      method: 'GET',
      path: '/claims/:expenseClaimId/attachments/:attachmentId',
      responses: {
        200: z.object({
          fileName: z.string(),
          mimeType: z.string(),
          contentBase64: z.string(),
        }),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Download one receipt',
      metadata: routeMeta('expense:read'),
    },

    /** Draft-only and self-only, mirroring upload — once submitted, evidence is part of the record. */
    deleteClaimAttachment: {
      method: 'DELETE',
      path: '/claims/:expenseClaimId/attachments/:attachmentId',
      responses: {
        200: expenseAttachmentSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Remove a receipt from a draft claim',
      metadata: routeMeta('expense:submit'),
    },

    createClaim: {
      method: 'POST',
      path: '/claims',
      headers: idempotencyHeadersSchema,
      body: createExpenseClaimBodySchema,
      responses: {
        201: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Create a draft expense claim for the caller, or (with expense.claim:file_for) naming someone else as the claimant',
      metadata: routeMeta(['expense:submit', 'expense.claim:file_for']),
    },

    /**
     * ADDED 2026-09-15 — closes the gap `createClaim`'s "Save as draft" opened without a way back
     * in: a draft could be created, but never reopened and finished. `expense:submit` over the
     * claim's own target (the same check `createClaim`/`submitClaim` already use) plus a
     * status==='draft' guard in the route — editing anything past that point would be rewriting the
     * figures a real approval workflow already started against.
     */
    updateClaim: {
      method: 'PATCH',
      path: '/claims/:expenseClaimId',
      body: updateExpenseClaimBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit a draft expense claim before submitting it',
      metadata: routeMeta('expense:submit'),
    },

    /**
     * ADDED 2026-09-12 as drafts-only, briefly relaxed to any status 2026-09-13 to clear
     * accumulated demo-account test data, then RESTORED to self-only + draft-only on 2026-09-17
     * (gap analysis EXP-16, fixing this comment to match) — `deleteOwnDraftClaim` in `expense.ts`
     * is the one definition both this route and `bulkDeleteClaims` go through, so the two can
     * never disagree about what may be deleted. A submitted claim's reference number and workflow
     * instance ARE the audit record; test data is cleared by resetting the database, never by a
     * route that can erase a decided claim.
     */
    deleteClaim: {
      method: 'DELETE',
      path: '/claims/:expenseClaimId',
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      // The handler has been draft-only and self-only again since 2026-09-17 — this summary said
      // "any status" until 2026-09-24, a stale leftover of the 2026-09-13 relaxation above.
      summary: 'Delete one of your own draft claims',
      metadata: routeMeta('expense:submit'),
    },

    submitClaim: {
      method: 'POST',
      path: '/claims/:expenseClaimId/submit',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /**
         * The category's `maxAmount` or `requiresReceipt` gate refused it. A hard gate at submit
         * rather than a workflow condition, so an over-limit claim never reaches an approver at all.
         */
        422: errorBodySchema,
      },
      summary: 'Submit a draft claim and start its approval workflow',
      metadata: routeMeta(['expense:submit', 'expense.claim:file_for']),
    },

    /**
     * ADDED 2026-09-17 (row 13, REIMBURSEMENT_STATUS.md) — the claimant pulling their own claim
     * back. `withdrawn` was already a recognised status and `WorkflowStatus` mapping; nothing ever
     * triggered it. Self-only, deliberately narrower than `expense:submit`'s scope alone allows —
     * same reasoning as `updateClaim`/`deleteClaim`'s explicit self-only checks.
     */
    withdrawClaim: {
      method: 'POST',
      path: '/claims/:expenseClaimId/withdraw',
      headers: idempotencyHeadersSchema,
      body: withdrawClaimBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not `draft` or `pending` — already decided (approved/rejected) or already settled. */
        409: errorBodySchema,
      },
      summary: 'Withdraw a draft or pending claim before it is decided',
      metadata: routeMeta('expense:submit'),
    },

    /**
     * EXP-P1-9 — stops an `approved` claim before it joins a payment batch or payroll run; no
     * route existed for this at all before. Claimant-or-filer only, same check withdraw/resubmit
     * already use — not a finance/approver action, since nothing here touches money that has
     * already moved (it can't have: the gate below refuses once the claim is batched/reserved).
     * A claim already in a batch or payroll run must be removed from THAT first — a different
     * problem (EXP-P1-10/11), not a plain cancel.
     */
    cancelClaim: {
      method: 'POST',
      path: '/claims/:expenseClaimId/cancel',
      headers: idempotencyHeadersSchema,
      body: cancelClaimBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        /** Not `approved`, or already joined a payment batch / reserved by a payroll run. */
        409: errorBodySchema,
      },
      summary: 'Cancel an approved claim before it is paid',
      metadata: routeMeta('expense:submit'),
    },

    approveClaim: {
      method: 'POST',
      path: '/claims/:expenseClaimId/approve',
      headers: idempotencyHeadersSchema,
      body: decideExpenseClaimBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        /**
         * Approving an already-REJECTED claim reopens it (2026-09-13) — an override for someone
         * without `workflow:manage`, the permission the reopen path itself requires.
         */
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** No workflow instance, or no step currently awaiting a decision (already decided). */
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller — or reopen a rejected claim',
      metadata: routeMeta(null),
    },

    rejectClaim: {
      method: 'POST',
      path: '/claims/:expenseClaimId/reject',
      headers: idempotencyHeadersSchema,
      body: decideExpenseClaimBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    /**
     * ADDED 2026-09-17 (row 16) — the same decision `approveClaim`/`rejectClaim` make, applied to
     * a selection. Runs each claim through the identical `decideExpenseClaim` path (so
     * `@hrms/workflow` still owns every decision — invariant 6 — and nothing here hand-rolls a
     * status flip), collecting per-claim outcomes rather than aborting the set on the first
     * failure. Always 200 when the request itself was well-formed: "6 of these 100 could not be
     * decided" is a result, not an error.
     */
    bulkDecideClaims: {
      method: 'POST',
      path: '/claims/bulk-decide',
      headers: idempotencyHeadersSchema,
      body: bulkDecideExpenseClaimsBodySchema,
      responses: {
        200: bulkDecideExpenseClaimsResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Approve or reject several claims in one action',
      metadata: routeMeta(null),
    },

    /**
     * R-P5 (Keka gap row 18) — a lighter action than `rejectClaim`: the current approver asks for
     * changes instead of deciding. Unlike a rejection (terminal; `resubmitClaim` clones it into a
     * fresh claim because `WorkflowInstance`'s unique index refuses a second instance for the same
     * id), this SAME claim cycles back to `draft` — editable via the existing `updateClaim`,
     * resubmittable via the existing `submitClaim`. That's possible because the current instance is
     * retired via `supersedeInstance` (`@hrms/workflow`'s F10 mechanism, `packages/db/prisma/
     * schema.prisma`'s own `WorkflowInstance.entityId` comment) rather than left active — the same
     * general escape hatch `payroll-runs.ts`'s reopen already uses, applied here for the first time.
     */
    sendClaimBack: {
      method: 'POST',
      path: '/claims/:expenseClaimId/send-back',
      headers: idempotencyHeadersSchema,
      body: sendClaimBackBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        /** No workflow instance, or no step currently awaiting a decision (already decided). */
        412: errorBodySchema,
      },
      summary: 'Send the claim back to the claimant as a draft, asking for changes',
      metadata: routeMeta(null),
    },

    bulkDeleteClaims: {
      method: 'POST',
      path: '/claims/bulk-delete',
      body: bulkDeleteExpenseClaimsBodySchema,
      responses: {
        200: bulkDeleteExpenseClaimsResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Delete several of your own draft claims in one action',
      metadata: routeMeta('expense:submit'),
    },

    listDecisions: {
      method: 'GET',
      path: '/decisions',
      query: listExpenseDecisionsQuerySchema,
      responses: {
        200: paginatedSchema(expenseDecisionSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "An approver's history of claims they approved or rejected — the caller's own by default",
      metadata: routeMeta(null),
    },

    /**
     * ADDED 2026-09-17 (row 25) — `rejected` was terminal; nothing let a claimant try again.
     *
     * WHY A NEW CLAIM, NOT THE OLD ONE REOPENED: `WorkflowInstance` carries a hard
     * `@@unique([orgId, entityType, entityId])` — one instance ever, for the entity's whole
     * lifetime, regardless of status. A second `startWorkflow` call against the SAME claim id is
     * refused outright (`ConflictError`, confirmed live). Resetting the rejected claim back to
     * `draft` in place and calling `submitClaim` again therefore cannot work without changing the
     * shared engine — out of scope for a reimbursement-only change. Creating a fresh claim (its own
     * id, its own reference, its own instance) needs no engine change at all, and arguably keeps a
     * *better* audit trail than mutating the rejected row would have: `CLM-2026-00042` stays
     * exactly what it always was — rejected, with its reason, permanently — and the new attempt
     * earns its own number rather than erasing that history in place.
     *
     * Copies every claim-shape field (category, date, amount, currency, GST fields, merchant,
     * paid mode, business purpose) from the rejected claim into a brand-new `draft` — same shape
     * `createClaim` produces, pre-filled instead of blank. The employee edits via the existing
     * `updateClaim` and sends via the existing `submitClaim`, exactly like any other draft.
     */
    resubmitClaim: {
      method: 'POST',
      path: '/claims/:expenseClaimId/resubmit',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        201: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not `rejected` — still pending, a draft, approved, settled, or already withdrawn. */
        409: errorBodySchema,
      },
      summary: 'Create a new draft claim pre-filled from a rejected one',
      metadata: routeMeta('expense:submit'),
    },

    /**
     * ADDED 2026-09-13 — the finance action after approval, per this file's original header
     * ("settlement... later task group"). Requires `expense:settle`, which only the `finance` role
     * holds (roles.ts) — a manager who approved the claim cannot also settle it themselves.
     */
    settleClaim: {
      method: 'POST',
      path: '/claims/:expenseClaimId/settle',
      headers: idempotencyHeadersSchema,
      body: settleClaimBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not currently `approved` — already settled, still pending, rejected, or a draft. */
        409: errorBodySchema,
        /**
         * ADDED 2026-09-17 — `payoutMode: 'payroll_add_on'` is refused here. There is no
         * `@hrms/payroll` package in this repository; nothing reads that flag and nothing will
         * ever act on it. Before this, choosing it closed the claim as `settled` and told the
         * employee (via `expense.settled`) that the amount was riding along in their next payroll
         * run — a promise no system in this codebase can keep. Refuse it at the one route that
         * writes `payoutMode`, rather than only removing it from the web/mobile dropdowns, so it
         * is closed on every client at once, present and future.
         */
        422: errorBodySchema,
      },
      summary: 'Record that an approved claim has been paid out',
      metadata: routeMeta('expense:settle'),
    },

    /**
     * EXP-P2-14 (Keka parity) — a bank bounce/return on THIS claim's own payout leg (wrong
     * account, account closed), reported after the fact. Walks the claim back to `approved` — the
     * BATCH it may have ridden in on stays `paid` (ExpensePaymentBatch's own header: deliberately
     * no route back from `paid` AT THE BATCH LEVEL; this is one claim's leg, not the batch run).
     */
    reportClaimPaymentFailure: {
      method: 'POST',
      path: '/claims/:expenseClaimId/payment-failed',
      headers: idempotencyHeadersSchema,
      body: reportClaimPaymentFailureBodySchema,
      responses: {
        200: expenseClaimSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not currently `settled`, was paid via `payoutMode: 'advance'` (no real money moved to
         * fail), or netted against an advance (reversing that net-off safely isn't built yet). */
        409: errorBodySchema,
      },
      summary: "Report that a settled claim's payment bounced — reverts it to approved",
      metadata: routeMeta('expense:settle'),
    },

    // -------------------------------------------------------------------------
    // Advances — TG-5
    // -------------------------------------------------------------------------

    /** Defaults to the caller's own advances; anyone else's requires `expense.advance:manage` or
     * (for the approvals view) `expense:approve` — same split `listClaims`/`listApprovals` make. */
    listAdvances: {
      method: 'GET',
      path: '/advances',
      query: listExpenseAdvancesQuerySchema,
      responses: {
        200: paginatedSchema(expenseAdvanceSchema),
        401: errorBodySchema,
      },
      summary: "List advances — one's own, or (with expense.advance:manage) everyone's",
      metadata: routeMeta(null),
    },

    getAdvance: {
      method: 'GET',
      path: '/advances/:advanceId',
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one advance',
      // No single permission is unconditionally required — the handler allows the caller's own
      // advance through regardless of grants, same as approveAdvance/rejectAdvance's own reasoning.
      metadata: routeMeta(null),
    },

    createAdvance: {
      method: 'POST',
      path: '/advances',
      headers: idempotencyHeadersSchema,
      body: createExpenseAdvanceBodySchema,
      responses: {
        201: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Draft an advance request, or issue one for someone else',
      // Any-of, since 2026-09-24: self-request OR issuing one for someone else — the handler
      // checks exactly one of the two depending on `body.personId`, never both, never neither.
      metadata: routeMeta(['expense.advance:request', 'expense.advance:issue']),
    },

    updateAdvance: {
      method: 'PATCH',
      path: '/advances/:advanceId',
      body: updateExpenseAdvanceBodySchema,
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not a `draft` — already submitted or decided. */
        409: errorBodySchema,
      },
      summary: 'Edit a draft advance request',
      metadata: routeMeta('expense.advance:request'),
    },

    deleteAdvance: {
      method: 'DELETE',
      path: '/advances/:advanceId',
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a draft advance request',
      metadata: routeMeta('expense.advance:request'),
    },

    submitAdvance: {
      method: 'POST',
      path: '/advances/:advanceId/submit',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** No applicable approval workflow for the caller's legal entity — same `NO_APPROVAL_
         * WORKFLOW` shape every other `submit*` route in this codebase already answers with. */
        412: errorBodySchema,
      },
      summary: 'Submit a draft advance request and start its approval workflow',
      // Any-of, matching createAdvance's own reasoning: the shared submitExpenseAdvance this
      // route calls accepts either the requester or an issuer, even though createAdvance's own
      // issue path always submits inline today and never leaves this route anything to do for it.
      metadata: routeMeta(['expense.advance:request', 'expense.advance:issue']),
    },

    withdrawAdvance: {
      method: 'POST',
      path: '/advances/:advanceId/withdraw',
      headers: idempotencyHeadersSchema,
      body: withdrawAdvanceBodySchema,
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not `draft` or `pending` — already decided, or already disbursed. */
        409: errorBodySchema,
      },
      summary: 'Withdraw a draft or pending advance request before it is decided',
      metadata: routeMeta('expense.advance:request'),
    },

    approveAdvance: {
      method: 'POST',
      path: '/advances/:advanceId/approve',
      headers: idempotencyHeadersSchema,
      body: decideExpenseAdvanceBodySchema,
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** No workflow instance, or no step currently awaiting the caller's decision. */
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    rejectAdvance: {
      method: 'POST',
      path: '/advances/:advanceId/reject',
      headers: idempotencyHeadersSchema,
      body: decideExpenseAdvanceBodySchema,
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    /**
     * EXP-P2-9 — the same decision `approveAdvance`/`rejectAdvance` make, applied to a selection.
     * Mirrors `bulkDecideClaims` exactly: each advance decided independently, one failure never
     * aborts the rest.
     */
    bulkDecideAdvances: {
      method: 'POST',
      path: '/advances/bulk-decide',
      headers: idempotencyHeadersSchema,
      body: bulkDecideExpenseAdvancesBodySchema,
      responses: {
        200: bulkDecideExpenseAdvancesResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Approve or reject several advance requests in one action',
      metadata: routeMeta(null),
    },

    /** The finance action after approval — pays the advance out for real and opens its balance.
     * Requires `expense.advance:manage`, never held by whoever approved the request, same
     * maker-checker split `expense:settle` already enforces against `expense:approve`. */
    disburseAdvance: {
      method: 'POST',
      path: '/advances/:advanceId/disburse',
      headers: idempotencyHeadersSchema,
      body: disburseAdvanceBodySchema,
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not currently `approved` — already disbursed, still pending, rejected, or a draft. */
        409: errorBodySchema,
      },
      summary: 'Disburse an approved advance and open its outstanding balance',
      metadata: routeMeta('expense.advance:manage'),
    },

    /** Writes off whatever balance remains — the employee keeps what they were already paid, and
     * the difference is the company's own loss to book, not the employee's debt to chase. */
    closeAdvance: {
      method: 'POST',
      path: '/advances/:advanceId/close',
      headers: idempotencyHeadersSchema,
      body: closeAdvanceBodySchema,
      responses: {
        200: expenseAdvanceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not `disbursed` — nothing outstanding to write off yet, or already closed/settled. */
        409: errorBodySchema,
      },
      summary: 'Write off an advance nobody will fully claim against',
      metadata: routeMeta('expense.advance:manage'),
    },

    listPaymentBatches: {
      method: 'GET',
      path: '/payment-batches',
      query: listExpensePaymentBatchesQuerySchema,
      responses: {
        200: paginatedSchema(expensePaymentBatchSchema),
        401: errorBodySchema,
      },
      summary: 'List NEFT/RTGS payment batches, for whoever holds expense:settle',
      metadata: routeMeta(null),
    },

    getPaymentBatch: {
      method: 'GET',
      path: '/payment-batches/:paymentBatchId',
      responses: {
        200: expensePaymentBatchSchema.extend({ claims: z.array(expenseClaimSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one payment batch and the claims in it',
      metadata: routeMeta('expense:settle'),
    },

    /**
     * Groups the given claims into one batch. All must be `approved`, unbatched, and share the
     * same currency and legal entity (this file's own header comment on why) — checked inside the
     * transaction, not by the body schema, which sees only ids.
     */
    createPaymentBatch: {
      method: 'POST',
      path: '/payment-batches',
      headers: idempotencyHeadersSchema,
      body: createExpensePaymentBatchBodySchema,
      responses: {
        201: expensePaymentBatchSchema.extend({ claims: z.array(expenseClaimSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        /** A named claim was not found, not `approved`, already in another batch, or the set spans
         * more than one currency or legal entity. */
        422: errorBodySchema,
      },
      summary: 'Group approved claims into one NEFT/RTGS payment batch',
      metadata: routeMeta('expense:settle'),
    },

    /**
     * EXP-P1-10 — "a wrong draft batch holds its claims for good" (the review's own line): before
     * this, there was no way back out once a claim joined a batch, short of marking the whole
     * batch paid and sorting it out after the fact. DRAFT ONLY — once `generateBankFile` has run,
     * the file already names this claim (`getBankFile`'s own comment on why a batch's claim set is
     * stable from that point on); removing one after the fact would desync the file from reality,
     * a different problem this route does not attempt to solve. The claim returns to a plain
     * `approved` claim — eligible for another batch, individual settlement, or `cancelClaim` (EXP-
     * P1-9) — and the batch's own `totalAmount` is recomputed from whatever is left in it.
     */
    removeClaimFromPaymentBatch: {
      method: 'POST',
      path: '/payment-batches/:paymentBatchId/claims/:expenseClaimId/remove',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: expensePaymentBatchSchema.extend({ claims: z.array(expenseClaimSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The batch is not `draft`, or this claim is not actually in it. */
        409: errorBodySchema,
      },
      summary: 'Take one claim back out of a draft payment batch',
      metadata: routeMeta('expense:settle'),
    },

    /**
     * Generates the NEFT/RTGS upload file from every claim in the batch and stores it in Spaces.
     * Refuses the WHOLE batch, naming the person, if any claimant has no bank account on file —
     * never emits a file with a blank beneficiary row.
     */
    generateBankFile: {
      method: 'POST',
      path: '/payment-batches/:paymentBatchId/bank-file',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        /**
         * `csvContent` carries the file itself, inline — not only a stored object key. Object
         * storage (DO Spaces) is best-effort here: `bankFileGenerated` reflects that the CSV was
         * produced, not that it landed in Spaces, so Finance can always download it from this
         * response even in an environment with no Spaces credentials configured.
         */
        200: expensePaymentBatchSchema.extend({ csvContent: z.string() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The batch is `paid` — nothing left to generate a file for. Calling this again on a
         * `draft` or `file_generated` batch is fine (and expected): it re-derives the same file
         * from the same unchangeable set of claims, which is how resuming a batch redownloads it. */
        409: errorBodySchema,
        /** One or more claimants have no `BankAccount` on file. `errorBodySchema`'s `details`
         * carries their names so Finance knows exactly who to chase before retrying. */
        422: errorBodySchema,
      },
      summary: 'Generate (or re-download) the NEFT/RTGS bank file for this batch',
      metadata: routeMeta('expense:settle'),
    },

    /**
     * Read-only counterpart of `generateBankFile`: hands back the bank file of a batch whose file
     * has already been generated — including a `paid` one, which `generateBankFile` refuses. It never
     * changes the batch. The file is rebuilt from the batch's claims (which can't change once
     * batched) and the claimants' bank details ON FILE NOW, so it can differ from what was uploaded
     * if someone has since changed their account — the response says nothing about that, the screen
     * does. 409 for a `draft` batch, which has no file yet.
     */
    getBatchBankFile: {
      method: 'GET',
      path: '/payment-batches/:paymentBatchId/bank-file',
      responses: {
        200: expensePaymentBatchSchema.extend({ csvContent: z.string() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Re-download the bank file of a batch that has already generated one, paid or not',
      metadata: routeMeta('expense:settle'),
    },

    /**
     * Finance confirms the bank actually moved the money. Settles every claim in the batch in one
     * transaction, writing the batch's own reference into each claim's `payoutReference`. No route
     * back from `paid` — same reasoning as a claim's `reference` never being reused.
     *
     * EXP-P1-11 — an INR batch still needs `file_generated` first, as before. A non-INR batch has
     * no NEFT/RTGS file to generate at all (an India-domestic rail — `generateBankFile`'s own
     * comment), so it goes straight from `draft` to `paid` here instead — Finance pays it some
     * other way (wire transfer, SWIFT) outside this system and confirms it the same way a single
     * non-batched claim settles with no bank file.
     */
    markBatchPaid: {
      method: 'POST',
      path: '/payment-batches/:paymentBatchId/mark-paid',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        200: expensePaymentBatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not the required status for this batch's currency — see this route's own comment: INR
         * wants `file_generated`, anything else wants `draft`. Also 409 once already `paid`. */
        409: errorBodySchema,
      },
      summary: 'Confirm a batch was paid (a generated file for INR, directly for any other currency), and settle every claim in it',
      metadata: routeMeta('expense:settle'),
    },
  },
  { pathPrefix: '/expense' },
);
